import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { btnPrimarySx, btnDisabledSx } from '../../../utils/buttonSx';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { RootState } from '../../../redux/store';
import axios from '../../../interceptors/axios';
import { ThumbImg, isVideo as isVideoMime } from '../media/MediaCard';
import MediaBrowserPanel, { BrowserFile } from './MediaBrowserPanel';
import { usePageHeader } from '../../../context/PageHeaderContext';
import { scrollAreaSx } from '../../../utils/scrollSx';
import { useConfirm } from '../../../hooks/useConfirm';

// ── Types ──────────────────────────────────────────────────────────────────────
interface PlaylistCard { id: number; name: string; itemCount: number; totalDurationMs: number; videoCount: number; imageCount: number; updatedAt: string; }
interface PlaylistItem { mediaFileId: number; orderIndex: number; rotationIndex?: number; groupName?: string | null; groupKey?: number; durationOverrideMs: number | null; scaleMode: string; fileName: string; mimeType: string; durationMs: number | null; sizeBytes?: number; width?: number | null; height?: number | null; videoCodec?: string | null; bitDepth?: number | null; }
interface SmartFile { name: string; durationMs: number; manualPlays: number; video: boolean; mediaFileId?: number; }
interface AvailabilitySlot { dayOfWeek: number; startTime: string; endTime: string; }
interface FileAvailability { slots: AvailabilitySlot[]; alwaysAvailable: boolean; }

// ── Helpers ───────────────────────────────────────────────────────────────────
const isVid = (mime: string) => isVideoMime(mime);
const fmtMs = (ms: number) => {
  const s = Math.round(ms / 1000);
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  if (h > 0) return `${h}:${m.toString().padStart(2,'0')}:${sec.toString().padStart(2,'0')}`;
  return `${m}:${sec.toString().padStart(2,'0')}`;
};
const fmtSizeMB = (b: number) => `${(b / 1024 / 1024).toFixed(1)} MB`;
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
const itemDur = (item: PlaylistItem) => item.durationOverrideMs ?? item.durationMs ?? 10000;
const parseDurationMs = (raw: number | null | undefined): number => { if (!raw || raw <= 0) return 10000; if (raw < 1000) return Math.round(raw * 1000); return raw; };

// ── Glass sx ──────────────────────────────────────────────────────────────────
const glass = { background: 'var(--glass-bg)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid var(--glass-border)' } as const;

// ── Icons ─────────────────────────────────────────────────────────────────────
const IcoPlus    = () => <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const IcoBack    = () => <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>;
const IcoStar    = () => <svg width={11} height={11} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/></svg>;
const IcoX       = () => <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const IcoClock   = () => <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
const IcoUp      = () => <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><polyline points="18 15 12 9 6 15"/></svg>;
const IcoDown    = () => <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>;
const IcoChevRight = () => <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>;
const IcoChevDown  = () => <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>;
const IcoPencil    = () => <svg width={9} height={9} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
const IcoCal     = () => <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
const IcoVideo   = () => <svg width={8} height={8} viewBox="0 0 24 24" fill="rgba(255,255,255,.5)"><polygon points="5 3 19 12 5 21 5 3"/></svg>;
const IcoImage   = () => <svg width={8} height={8} viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.5)" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5" fill="rgba(255,255,255,.5)" stroke="none"/><polyline points="21 15 16 10 5 21"/></svg>;

// ── Availability Calendar popup ───────────────────────────────────────────────
const DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

const AvailabilityPopup: React.FC<{ fileId: number }> = ({ fileId }) => {
  const [data, setData] = useState<FileAvailability | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    axios.get<FileAvailability>(`/media/files/${fileId}/availability`)
      .then(r => { if (active) setData(r.data); })
      .catch(() => { if (active) setData({ slots: [], alwaysAvailable: true }); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [fileId]);

  return (
    <Box sx={{ position: 'absolute', bottom: 'calc(100% + 8px)', right: 0, zIndex: 9999, width: 240, ...glass, borderRadius: '12px', p: '12px', boxShadow: '0 12px 32px rgba(0,0,0,.45)', pointerEvents: 'none' }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--text-primary)', mb: '8px', textTransform: 'uppercase', letterSpacing: '.06em' }}>Availability</Typography>
      {loading ? (
        <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>Loading…</Typography>
      ) : data?.alwaysAvailable || !data?.slots.length ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#4ade80', flexShrink: 0 }} />
          <Typography sx={{ fontSize: 12, color: '#4ade80' }}>Always available</Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '3px', mb: '6px' }}>
            {DAYS.map((d,i) => {
              const active = data.slots.some(s => s.dayOfWeek === i+1);
              return (
                <Box key={d} sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontSize: 9, fontWeight: 700, color: active?'var(--accent-light)':'var(--text-disabled)', textTransform: 'uppercase', mb: '2px' }}>{d}</Typography>
                  <Box sx={{ height: 22, borderRadius: '4px', background: active?'rgba(99,102,241,.3)':'rgba(255,255,255,.04)', border: `1px solid ${active?'rgba(99,102,241,.5)':'var(--glass-border)'}` }} />
                </Box>
              );
            })}
          </Box>
          {data.slots.map((s, i) => (
            <Typography key={i} sx={{ fontSize: 11, color: 'var(--text-secondary)' }}>{DAYS[(s.dayOfWeek-1)%7]}: {s.startTime} – {s.endTime}</Typography>
          ))}
        </>
      )}
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
type View = 'cards' | 'editor' | 'smart';

const PlaylistsPage2: React.FC = () => {
  const { t } = useTranslation();
  const auth = useSelector((s: RootState) => s.auth.value);
  const navigate = useNavigate();
  const { id: paramId } = useParams<{ id: string }>();
  const { setBreadcrumbs, setActionConfig } = usePageHeader();
  const isNarrow = useMediaQuery('(max-width:899px)');
  const { confirm, confirmModal } = useConfirm();
  const [view, setView] = useState<View>('cards');
  const [selectedId, setSelectedId] = useState<number | null>(null);

  // Cards
  const [cards, setCards] = useState<PlaylistCard[]>([]);
  const [blockMsg, setBlockMsg] = useState<string | null>(null);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [listView, setListView] = useState(false);
  const isMobile = useMediaQuery('(max-width:600px)');
  const fetchCards = useCallback(async () => { setCardsLoading(true); try { const { data } = await axios.get<PlaylistCard[]>('/playlists'); setCards(data); } catch {} finally { setCardsLoading(false); } }, []);
  useEffect(() => { if (auth) fetchCards(); }, [auth, fetchCards]);

  const handleCreate = async () => {
    setCreating(true);
    try { const { data } = await axios.post<PlaylistCard>('/playlists', { name: 'New Playlist' }); setCards(prev => [data, ...prev]); navigate(`/playlists/${data.id}`); }
    catch {} finally { setCreating(false); }
  };

  // Editor state
  const [editorName, setEditorName] = useState('');
  const [items, setItems] = useState<PlaylistItem[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [defaultScale, setDefaultScale] = useState<'fill'|'fit'|'stretch'>('fill');
  const [defaultImgDurSec, setDefaultImgDurSec] = useState(10);
  const [description, setDescription] = useState('');
  const [transitionType, setTransitionType] = useState<'cut'|'fade'|'slide'>('cut');
  const [transitionDurationMs, setTransitionDurationMs] = useState(500);
  const [noBlackGap, setNoBlackGap] = useState(false);
  const [randomPlayback, setRandomPlayback] = useState(false);
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [calHover, setCalHover] = useState<number | null>(null);
  const [editingCardId, setEditingCardId] = useState<number | null>(null);
  const [editingCardName, setEditingCardName] = useState('');
  const [mobileBrowserOpen, setMobileBrowserOpen] = useState(false);
  const cardNameInputRef = useRef<HTMLInputElement>(null);

  const startCardRename = (e: React.MouseEvent, id: number, name: string) => {
    e.stopPropagation();
    setEditingCardId(id); setEditingCardName(name);
    setTimeout(() => cardNameInputRef.current?.select(), 0);
  };
  const commitCardRename = async (id: number) => {
    const name = editingCardName.trim();
    setEditingCardId(null);
    if (!name) return;
    try {
      await axios.patch(`/playlists/${id}`, { name });
      setCards(prev => prev.map(c => c.id === id ? { ...c, name } : c));
    } catch {}
  };

  useEffect(() => {
    if (view === 'cards') setBreadcrumbs([{ label: t('playlists.title') }]);
    else if (!isMobile) {
      // Desktop: untouched
      if (view === 'editor') setBreadcrumbs([{ label: t('playlists.title') }]);
      else setBreadcrumbs([{ label: t('playlists.title') }, { label: t('playlists.smartGenerator') }]);
    }
    // Mobile: header back arrow needs a clickable parent segment
    else if (view === 'editor') setBreadcrumbs([
      { label: t('playlists.title'), onClick: () => navigate('/playlists') },
      { label: editorName || '…' },
    ]);
    else setBreadcrumbs([
      { label: editorName || t('playlists.title'), onClick: () => setView('editor') },
      { label: t('playlists.smartGenerator') },
    ]);
    return () => setBreadcrumbs([]);
  }, [view, editorName, isMobile, navigate, setBreadcrumbs]);

  // Mobile: the create button moves into the topbar, and only on the cards view
  useEffect(() => {
    if (!isNarrow || view !== 'cards') { setActionConfig(null); return; }
    setActionConfig({ label: 'New Playlist', onClick: handleCreate, disabled: creating });
    return () => setActionConfig(null);
  }, [isNarrow, view, creating, setActionConfig]); // eslint-disable-line react-hooks/exhaustive-deps
  const [calCache, setCalCache] = useState<Record<number, boolean>>({});
  const calFetching = useRef(new Set<number>());
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [bulkDurSec, setBulkDurSec] = useState('');
  const [bulkAction, setBulkAction] = useState<'scale' | 'duration' | ''>('');

  // Groups
  const [collapsedGroups, setCollapsedGroups] = useState<Set<number>>(new Set());
  const [editingGroupKey, setEditingGroupKey] = useState<number | null>(null);
  const [gapGk, setGapGk] = useState<number | null>(null);

  // Drag
  const draggedMediaFile = useRef<BrowserFile[]>([]);
  const dragIdx = useRef<number | null>(null);
  const dragByHeader = useRef(false);
  const [dropTarget, setDropTarget] = useState<{ idx: number; pos: 'before' | 'after' } | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const dragScrollDir = useRef(0);
  const dragScrollRaf = useRef<number | null>(null);
  const [dropZoneActive, setDropZoneActive] = useState(false);

  // Sync URL ↔ view: when URL has :id → editor; when /playlists → cards
  useEffect(() => {
    if (paramId) {
      const numId = parseInt(paramId, 10);
      if (!isNaN(numId) && numId !== selectedId) openEditor(numId, '');
      else if (!isNaN(numId) && view === 'cards') setView('editor');
    } else {
      if (view !== 'cards') { setView('cards'); }
    }
  }, [paramId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openEditor = useCallback(async (id: number, name: string, preItems?: PlaylistItem[]) => {
    setSelectedId(id); setEditorName(name); setView('editor'); setSelectedItems(new Set());
    if (preItems) { setItems(preItems); return; }
    setItemsLoading(true);
    try {
      const { data } = await axios.get<{ name: string; description?: string | null; transitionType?: string; transitionDurationMs?: number; noBlackGap?: boolean; randomPlayback?: boolean; syncEnabled?: boolean; items: PlaylistItem[] }>(`/playlists/${id}`);
      setEditorName(data.name);
      setDescription(data.description ?? '');
      setTransitionType((data.transitionType as 'cut'|'fade'|'slide') ?? 'cut');
      setTransitionDurationMs(data.transitionDurationMs ?? 500);
      setNoBlackGap(data.noBlackGap ?? false);
      setRandomPlayback(data.randomPlayback ?? false);
      setSyncEnabled(data.syncEnabled ?? false);
      // Несколько строк с одним orderIndex — это группа ротации
      const raw = data.items.slice().sort((a, b) => a.orderIndex - b.orderIndex || (a.rotationIndex ?? 0) - (b.rotationIndex ?? 0));
      const perSlot = new Map<number, number>();
      raw.forEach(i => perSlot.set(i.orderIndex, (perSlot.get(i.orderIndex) ?? 0) + 1));
      setItems(raw.map(i => ({ ...i, groupKey: (perSlot.get(i.orderIndex) ?? 0) > 1 ? i.orderIndex : undefined })));
    }
    catch {} finally { setItemsLoading(false); }
  }, []);

  const handleSave = async () => {
    if (!selectedId) return; setSaving(true);
    try {
      await Promise.all([
        axios.patch(`/playlists/${selectedId}`, { name: editorName, description: description || null, transitionType, transitionDurationMs, noBlackGap, randomPlayback, syncEnabled }),
        axios.put(`/playlists/${selectedId}/items`, {
          items: (() => {
            // Группа занимает одну позицию эфира: общий orderIndex,
            // rotationIndex задаёт, на каком проходе играет её файл
            let slot = -1, rot = 0;
            let prevKey: number | undefined;
            return items.map(it => {
              const inSameGroup = it.groupKey != null && it.groupKey === prevKey;
              if (inSameGroup) rot++; else { slot++; rot = 0; }
              prevKey = it.groupKey;
              return {
                mediaFileId: it.mediaFileId,
                orderIndex: slot,
                durationOverrideMs: it.durationOverrideMs,
                scaleMode: it.scaleMode,
                rotationIndex: rot,
                groupName: it.groupKey != null ? (it.groupName ?? null) : null,
              };
            });
          })(),
        }),
      ]);
      // notify all screens using this playlist (directly or via schedule)
      axios.post(`/screens/publish-playlist/${selectedId}`).catch(() => {});
      setSaved(true); setTimeout(() => setSaved(false), 2000);
      setCards(prev => prev.map(c => c.id === selectedId ? { ...c, name: editorName, itemCount: items.length, totalDurationMs: items.reduce((s,it) => s + itemDur(it), 0) } : c));
    } catch {} finally { setSaving(false); }
  };

  const addToPlaylist = (file: BrowserFile) => {
    const vid = isVid(file.mime_type);
    setItems(prev => [...prev, { mediaFileId: file.id, orderIndex: prev.length, durationOverrideMs: vid ? null : defaultImgDurSec * 1000, scaleMode: file.default_scale_mode || defaultScale, fileName: file.original_name, mimeType: file.mime_type, durationMs: file.duration_ms, sizeBytes: file.size_bytes, width: file.width, height: file.height }]);
    setSaved(false);
  };
  const removeItem = (idx: number) => {
    setItems(prev => prev.filter((_,i) => i !== idx));
    setSelectedItems(prev => { const next = new Set<number>(); prev.forEach(i => { if (i < idx) next.add(i); else if (i > idx) next.add(i - 1); }); return next; });
    setSaved(false);
  };
  const setScale = (idx: number, mode: string) => { setItems(prev => prev.map((it,i) => i === idx ? { ...it, scaleMode: mode } : it)); setSaved(false); };
  const adjDur = (idx: number, deltaMs: number) => { setItems(prev => prev.map((it,i) => i !== idx ? it : { ...it, durationOverrideMs: Math.max(1000, (it.durationOverrideMs ?? it.durationMs ?? 10000) + deltaMs) })); setSaved(false); };
  const [swapping, setSwapping] = useState<[number, number] | null>(null);

  const fetchCalInfo = (fileId: number) => {
    if (calCache[fileId] !== undefined || calFetching.current.has(fileId)) return;
    calFetching.current.add(fileId);
    axios.get<FileAvailability>(`/media/files/${fileId}/availability`)
      .then(r => setCalCache(prev => ({ ...prev, [fileId]: !r.data.alwaysAvailable && r.data.slots.length > 0 })))
      .catch(() => setCalCache(prev => ({ ...prev, [fileId]: false })))
      .finally(() => calFetching.current.delete(fileId));
  };

  const moveItem = (idx: number, dir: -1 | 1, asGroup?: boolean) => {
    const gk = items[idx]?.groupKey;
    if (gk != null) {
      if (asGroup) {
        // Move entire group as a unit: displace the item beyond the group boundary
        setItems(prev => {
          const groupIdxs = prev.map((it, i) => it.groupKey === gk ? i : -1).filter(i => i >= 0);
          const groupStart = groupIdxs[0]; const groupEnd = groupIdxs[groupIdxs.length - 1];
          if (dir === -1 && groupStart === 0) return prev;
          if (dir === 1 && groupEnd === prev.length - 1) return prev;
          const next = [...prev];
          if (dir === -1) { const [d] = next.splice(groupStart - 1, 1); next.splice(groupEnd, 0, d); }
          else            { const [d] = next.splice(groupEnd + 1, 1);   next.splice(groupStart, 0, d); }
          return next;
        });
      } else {
        // Move item within the group only — does not cross group boundary
        setItems(prev => {
          const groupIdxs = prev.map((it, i) => it.groupKey === gk ? i : -1).filter(i => i >= 0);
          const groupStart = groupIdxs[0]; const groupEnd = groupIdxs[groupIdxs.length - 1];
          const newIdx = idx + dir;
          if (newIdx < groupStart || newIdx > groupEnd) return prev;
          const next = [...prev];
          [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
          return next;
        });
      }
      setSaved(false);
      return;
    }
    // Non-group item: jump over any adjacent group as a whole to keep it intact
    setItems(prev => {
      const neighbor = prev[idx + dir];
      if (!neighbor) return prev;
      const next = [...prev];
      const [moved] = next.splice(idx, 1);
      const neighborGk = neighbor.groupKey;
      if (neighborGk != null) {
        const gIdxs = next.map((it, i) => it.groupKey === neighborGk ? i : -1).filter(i => i >= 0);
        next.splice(dir === -1 ? gIdxs[0] : gIdxs[gIdxs.length - 1] + 1, 0, moved);
      } else {
        next.splice(dir === -1 ? idx - 1 : idx + 1, 0, moved);
      }
      return next;
    });
    setSaved(false);
  };

  const toggleSelect = (idx: number) => setSelectedItems(prev => { const n = new Set(prev); n.has(idx) ? n.delete(idx) : n.add(idx); return n; });
  const clearSelection = () => setSelectedItems(new Set());
  // ── Группы ротации ─────────────────────────────────────────────────────────
  // Группа — идущие подряд элементы с общим ключом. За каждый проход плейлиста
  // играет следующий её файл, поэтому в эфире она занимает одну позицию.
  const slotNumbers = useMemo(() => {
    const out: number[] = [];
    let n = 0;
    let prevKey: number | undefined;
    items.forEach(it => {
      if (!(it.groupKey != null && it.groupKey === prevKey)) n++;
      prevKey = it.groupKey;
      out.push(n);
    });
    return out;
  }, [items]);

  const groupSelected = () => {
    const idxs = Array.from(selectedItems).sort((a, b) => a - b);
    if (idxs.length < 2) return;
    const chosen = idxs.map(i => items[i]);
    const key = Math.max(0, ...items.map(i => i.groupKey ?? 0)) + 1;
    // Если в выделении была готовая группа — сохраняем её имя, это «добавить в группу»
    const existing = chosen.find(i => i.groupName)?.groupName;
    const groupCount = new Set(items.filter(i => i.groupKey != null).map(i => i.groupKey)).size;
    const name = existing ?? t('playlists2.groupName', { n: groupCount + 1 });
    const rest = items.filter((_, i) => !selectedItems.has(i));
    const insertAt = items.slice(0, idxs[0]).filter((_, i) => !selectedItems.has(i)).length;
    setItems([
      ...rest.slice(0, insertAt),
      ...chosen.map(i => ({ ...i, groupKey: key, groupName: name })),
      ...rest.slice(insertAt),
    ]);
    clearSelection(); setSaved(false);
  };

  const ungroup = (key: number) => {
    setItems(prev => prev.map(i => i.groupKey === key ? { ...i, groupKey: undefined, groupName: null } : i));
    setSaved(false);
  };

  const renameGroup = (key: number, name: string) => {
    setItems(prev => prev.map(i => i.groupKey === key ? { ...i, groupName: name } : i));
    setSaved(false);
  };

  const selectAll = () => setSelectedItems(new Set(items.map((_, i) => i)));
  const bulkSetScale = (mode: string) => { setItems(prev => prev.map((it, i) => selectedItems.has(i) ? { ...it, scaleMode: mode } : it)); setSaved(false); };
  const bulkSetDuration = () => {
    const ms = Math.round(parseFloat(bulkDurSec) * 1000);
    if (!ms || ms < 1000) return;
    setItems(prev => prev.map((it, i) => selectedItems.has(i) && !isVid(it.mimeType) ? { ...it, durationOverrideMs: ms } : it));
    setSaved(false);
  };
  const bulkRemove = () => { setItems(prev => prev.filter((_, i) => !selectedItems.has(i))); setSelectedItems(new Set()); setSaved(false); };

  // Drag auto-scroll helpers
  const startScrollRaf = () => {
    if (dragScrollRaf.current !== null) return;
    const tick = () => {
      const box = listRef.current;
      if (box && dragScrollDir.current !== 0) { box.scrollTop += dragScrollDir.current * 10; dragScrollRaf.current = requestAnimationFrame(tick); }
      else { dragScrollRaf.current = null; }
    };
    dragScrollRaf.current = requestAnimationFrame(tick);
  };
  const stopScrollRaf = () => {
    if (dragScrollRaf.current !== null) { cancelAnimationFrame(dragScrollRaf.current); dragScrollRaf.current = null; }
    dragScrollDir.current = 0;
  };

  // Drag reorder
  const onItemDragStart = (e: React.DragEvent, idx: number, byHeader = false) => {
    dragIdx.current = idx; dragByHeader.current = byHeader; draggedMediaFile.current = []; e.dataTransfer.effectAllowed = 'move'; stopScrollRaf();
  };
  const onItemDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    if (dragIdx.current === idx) { setDropTarget(null); return; }
    // Suppress indicator on same-group items when dragging the entire group by header
    if (dragByHeader.current) {
      const fromGk = dragIdx.current !== null ? items[dragIdx.current]?.groupKey : undefined;
      if (fromGk != null && items[idx]?.groupKey === fromGk) { setDropTarget(null); return; }
    }
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const pos: 'before' | 'after' = e.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
    setDropTarget(prev => prev?.idx === idx && prev?.pos === pos ? prev : { idx, pos });
    // Edge auto-scroll for mouse drag
    const box = listRef.current;
    if (box) {
      const r = box.getBoundingClientRect();
      if (e.clientY < r.top + 60) { dragScrollDir.current = -1; startScrollRaf(); }
      else if (e.clientY > r.bottom - 60) { dragScrollDir.current = 1; startScrollRaf(); }
      else { dragScrollDir.current = 0; }
    }
  };
  /** Reorder without a DragEvent — shared by mouse drop and touch drop. */
  const applyItemMove = (from: number, toIdx: number, pos: 'before' | 'after', byHeader: boolean) => {
    const target = { idx: toIdx, pos };
    dragByHeader.current = byHeader;
    setItems(prev => reorderItems(prev, from, toIdx, target, byHeader));
    dragIdx.current = null; setSelectedItems(new Set()); setSaved(false);
  };

  const onItemDrop = (e: React.DragEvent, toIdx: number) => {
    e.preventDefault();
    stopScrollRaf();
    const target = dropTarget;
    setDropTarget(null);

    if (draggedMediaFile.current.length > 0) {
      // Drop from media browser at specific position
      const files = draggedMediaFile.current;
      draggedMediaFile.current = [];
      const toItem = items[toIdx];
      const toGk   = toItem?.groupKey ?? undefined;
      const toGName = toGk != null ? (toItem?.groupName ?? null) : null;
      const insertAt = target?.pos === 'after' ? toIdx + 1 : toIdx;
      const newItems: PlaylistItem[] = files.map(file => {
        const vid = isVid(file.mime_type);
        return {
          mediaFileId: file.id, orderIndex: 0,
          durationOverrideMs: vid ? null : defaultImgDurSec * 1000,
          scaleMode: file.default_scale_mode || defaultScale,
          fileName: file.original_name, mimeType: file.mime_type,
          durationMs: file.duration_ms, sizeBytes: file.size_bytes,
          width: file.width, height: file.height,
          ...(toGk != null ? { groupKey: toGk, groupName: toGName } : {}),
        };
      });
      setItems(prev => { const next = [...prev]; next.splice(insertAt, 0, ...newItems); return next; });
      setSaved(false);
      return;
    }

    const from = dragIdx.current;
    if (from === null) return;

    setItems(prev => reorderItems(prev, from, toIdx, target, dragByHeader.current));

    dragIdx.current = null; setSelectedItems(new Set()); setSaved(false);
  };

  /** Drop handler for the gap spacer between groups — always inserts outside any group. */
  const handleGapDrop = (e: React.DragEvent, groupEndIdx: number) => {
    e.preventDefault();
    stopScrollRaf();
    setGapGk(null);
    setDropTarget(null);
    const insertAt = groupEndIdx + 1;
    if (draggedMediaFile.current.length > 0) {
      const files = draggedMediaFile.current;
      draggedMediaFile.current = [];
      const newItems: PlaylistItem[] = files.map(file => {
        const vid = isVid(file.mime_type);
        return { mediaFileId: file.id, orderIndex: 0, durationOverrideMs: vid ? null : defaultImgDurSec * 1000, scaleMode: file.default_scale_mode || defaultScale, fileName: file.original_name, mimeType: file.mime_type, durationMs: file.duration_ms, sizeBytes: file.size_bytes, width: file.width, height: file.height };
      });
      setItems(prev => { const next = [...prev]; next.splice(insertAt, 0, ...newItems); return next; });
      setSaved(false);
    } else {
      const from = dragIdx.current;
      if (from !== null) {
        setItems(prev => {
          const fromItem = prev[from];
          const gk = fromItem?.groupKey;
          if (dragByHeader.current && gk != null) {
            // Move whole group after groupEndIdx
            const groupItems = prev.filter(it => it.groupKey === gk);
            const rest = prev.filter(it => it.groupKey !== gk);
            const removedBefore = prev.slice(0, groupEndIdx + 1).filter(it => it.groupKey === gk).length;
            const insertAt = Math.min(groupEndIdx + 1 - removedBefore, rest.length);
            const result = [...rest];
            result.splice(insertAt, 0, ...groupItems);
            return result;
          }
          // Single item: strip groupKey and insert after groupEndIdx
          const next = [...prev];
          const [rawMoved] = next.splice(from, 1);
          const movedItem = { ...rawMoved, groupKey: undefined, groupName: null };
          let at = groupEndIdx + 1;
          if (from < at) at -= 1;
          next.splice(at, 0, movedItem);
          return next;
        });
        dragIdx.current = null; setSelectedItems(new Set()); setSaved(false);
      }
    }
  };

  /** Pure move of one item (or a whole group) inside the list. */
  const reorderItems = (
    prev: PlaylistItem[], from: number, toIdx: number,
    target: { idx: number; pos: 'before' | 'after' } | null, byHeader: boolean,
  ): PlaylistItem[] => {
    {
      const fromItem = prev[from];
      const gk = fromItem?.groupKey;
      const toItem = prev[toIdx];

      if (gk != null) {
        if (byHeader) {
          // Header drag → move entire group as a unit
          if (toItem?.groupKey === gk) return prev;
          const groupItems = prev.filter(it => it.groupKey === gk);
          const withoutGroup = prev.filter(it => it.groupKey !== gk);
          const newToIdx = withoutGroup.indexOf(toItem);
          if (newToIdx === -1) return prev;
          let insertAt = target?.pos === 'after' ? newToIdx + 1 : newToIdx;
          insertAt = Math.max(0, Math.min(insertAt, withoutGroup.length));
          const result = [...withoutGroup];
          result.splice(insertAt, 0, ...groupItems);
          return result;
        }

        // Individual item drag from group
        if (toItem?.groupKey === gk) {
          // Intra-group reorder
          let insertAt = target?.pos === 'after' ? toIdx + 1 : toIdx;
          const next = [...prev];
          const [moved] = next.splice(from, 1);
          if (from < insertAt) insertAt -= 1;
          next.splice(insertAt, 0, moved);
          return next;
        }

        if (toItem?.groupKey != null) {
          // Move item into another group
          const toGk = toItem.groupKey;
          const toGroupName = prev.find(i => i.groupKey === toGk)?.groupName ?? null;
          const next = [...prev];
          const [rawMoved] = next.splice(from, 1);
          const movedItem = { ...rawMoved, groupKey: toGk, groupName: toGroupName };
          const gIdxs = next.map((it, i) => it.groupKey === toGk ? i : -1).filter(i => i >= 0);
          next.splice(target?.pos === 'after' ? gIdxs[gIdxs.length - 1] + 1 : gIdxs[0], 0, movedItem);
          return next;
        }

        // Drop outside any group → ungroup this item
        let insertAt = target?.pos === 'after' ? toIdx + 1 : toIdx;
        const next = [...prev];
        const [rawMoved] = next.splice(from, 1);
        const movedItem = { ...rawMoved, groupKey: undefined, groupName: null };
        if (from < insertAt) insertAt -= 1;
        next.splice(insertAt, 0, movedItem);
        return next;
      }

      // Regular item dropped on a group → add to that group, unless at group boundary
      if (toItem?.groupKey != null) {
        const toGk = toItem.groupKey;
        const toGroupName = prev.find(i => i.groupKey === toGk)?.groupName ?? null;
        const next = [...prev];
        const [rawMoved] = next.splice(from, 1);
        const movedItem = { ...rawMoved, groupKey: toGk, groupName: toGroupName };
        const gIdxs = next.map((it, i) => it.groupKey === toGk ? i : -1).filter(i => i >= 0);
        next.splice(target?.pos === 'after' ? gIdxs[gIdxs.length - 1] + 1 : gIdxs[0], 0, movedItem);
        return next;
      }

      // Regular item dropped on a regular item
      let insertAt = target?.pos === 'after' ? toIdx + 1 : toIdx;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      if (from < insertAt) insertAt -= 1;
      next.splice(insertAt, 0, moved);
      return next;
    }
  };

  // ── Touch reorder ─────────────────────────────────────────────────────────
  // HTML5 drag & drop does not exist on touch devices, and a `draggable` row also
  // swallows the swipe, so the list could not even be scrolled. Touch gets its own
  // long-press drag, and `draggable` is left to pointer devices only.
  const isTouch = useMediaQuery('(pointer: coarse)');
  const [touchDrag, setTouchDrag] = useState<{ from: number; byHeader: boolean } | null>(null);
  const pressTimer = useRef<number | null>(null);
  const pressStart = useRef<{ x: number; y: number } | null>(null);
  const dropTargetRef = useRef<{ idx: number; pos: 'before' | 'after' } | null>(null);
  dropTargetRef.current = dropTarget;

  const cancelPress = () => {
    if (pressTimer.current !== null) { window.clearTimeout(pressTimer.current); pressTimer.current = null; }
    pressStart.current = null;
  };

  const onItemTouchStart = (e: React.TouchEvent, idx: number, byHeader = false) => {
    const t = e.touches[0];
    if (!t) return;
    cancelPress();
    pressStart.current = { x: t.clientX, y: t.clientY };
    pressTimer.current = window.setTimeout(() => {
      pressTimer.current = null;
      dragIdx.current = idx; dragByHeader.current = byHeader; draggedMediaFile.current = [];
      setDropTarget(null);
      setTouchDrag({ from: idx, byHeader });
    }, 300);
  };

  // A swipe that starts on a row must stay a scroll, so the pending press is
  // dropped as soon as the finger travels
  const onItemTouchMove = (e: React.TouchEvent) => {
    if (touchDrag || !pressStart.current) return;
    const t = e.touches[0];
    if (!t) return;
    if (Math.abs(t.clientY - pressStart.current.y) > 8 || Math.abs(t.clientX - pressStart.current.x) > 8) cancelPress();
  };

  useEffect(() => {
    if (!touchDrag) return;
    const rowUnder = (y: number) => {
      const box = listRef.current;
      const x = box ? box.getBoundingClientRect().left + box.clientWidth / 2 : window.innerWidth / 2;
      const el = document.elementFromPoint(x, y) as HTMLElement | null;
      const row = el?.closest('[data-item-idx]') as HTMLElement | null;
      if (!row) return null;
      const idx = Number(row.dataset.itemIdx);
      if (Number.isNaN(idx)) return null;
      const r = row.getBoundingClientRect();
      return { idx, pos: (y < r.top + r.height / 2 ? 'before' : 'after') as 'before' | 'after' };
    };
    const onMove = (ev: TouchEvent) => {
      ev.preventDefault();                       // hold the list still while dragging
      const y = ev.touches[0]?.clientY;
      if (y === undefined) return;
      const hit = rowUnder(y);
      if (hit && hit.idx !== touchDrag.from) {
        setDropTarget(prev => (prev?.idx === hit.idx && prev.pos === hit.pos ? prev : hit));
      }
      const box = listRef.current;              // auto-scroll near the edges
      if (box) {
        const r = box.getBoundingClientRect();
        if (y < r.top + 48) box.scrollTop -= 12;
        else if (y > r.bottom - 48) box.scrollTop += 12;
      }
    };
    const onEnd = () => {
      const target = dropTargetRef.current;
      if (target && target.idx !== touchDrag.from) {
        applyItemMove(touchDrag.from, target.idx, target.pos, touchDrag.byHeader);
      }
      dragIdx.current = null;
      stopScrollRaf();
      setDropTarget(null);
      setTouchDrag(null);
    };
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
    document.addEventListener('touchcancel', onEnd);
    return () => {
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onEnd);
    };
  }, [touchDrag]); // eslint-disable-line react-hooks/exhaustive-deps
  const onPlaylistZoneDragOver = (e: React.DragEvent) => { if (draggedMediaFile.current.length > 0 || dragIdx.current !== null) { e.preventDefault(); if (draggedMediaFile.current.length > 0) { e.dataTransfer.dropEffect = 'copy'; setDropZoneActive(true); } } };
  const onPlaylistZoneDrop = (e: React.DragEvent) => { e.preventDefault(); setDropZoneActive(false); if (draggedMediaFile.current.length > 0) { draggedMediaFile.current.forEach(addToPlaylist); draggedMediaFile.current = []; } };

  // Media browser drag
  const onMediaDragStart = (e: React.DragEvent, files: BrowserFile[]) => { draggedMediaFile.current = files; dragIdx.current = null; e.dataTransfer.effectAllowed = 'copy'; };
  const onMediaDragEnd = () => { draggedMediaFile.current = []; setDropZoneActive(false); };

  const totalDurMs = items.reduce((s, it) => s + itemDur(it), 0);

  // Smart generator
  const [smartFiles, setSmartFiles] = useState<SmartFile[]>([]);
  const [smartMinutes, setSmartMinutes] = useState(10);
  const [smartDupWarn, setSmartDupWarn] = useState<string | null>(null);

  const smartTotalMs = smartMinutes * 60 * 1000;
  const smartTotalSec = smartMinutes * 60;
  // plays for auto files: remaining time shared equally (by rounds)
  const smartManualMs = smartFiles.filter(f => f.manualPlays > 0).reduce((s,f) => s + f.manualPlays * f.durationMs, 0);
  const smartAutoFiles = smartFiles.filter(f => f.manualPlays === 0);
  const smartAutoOnceMs = smartAutoFiles.reduce((s,f) => s + f.durationMs, 0);
  const smartAutoRounds = smartAutoOnceMs > 0 ? Math.floor(Math.max(0, smartTotalMs - smartManualMs) / smartAutoOnceMs) : 0;
  const smartCalcN = (sf: SmartFile) => sf.manualPlays > 0 ? sf.manualPlays : smartAutoRounds;
  // max for file i = floor((totalMs - time used by OTHER manual files) / fileDuration)
  const smartMaxPlays = (idx: number) => {
    const otherMs = smartFiles.reduce((s, f, k) => k !== idx && f.manualPlays > 0 ? s + f.manualPlays * f.durationMs : s, 0);
    return Math.floor(Math.max(0, smartTotalMs - otherMs) / Math.max(1000, smartFiles[idx]?.durationMs ?? 1000));
  };
  const smartTotalFiles = smartFiles.reduce((s,f) => s + smartCalcN(f), 0);
  const smartActualTotalMs = smartFiles.reduce((s,f) => s + smartCalcN(f) * f.durationMs, 0);

  // Clamp manualPlays when duration decreases
  useEffect(() => {
    setSmartFiles(prev => {
      const clamped = prev.map(f => {
        if (f.manualPlays === 0) return f;
        const max = Math.floor(smartTotalMs / Math.max(1000, f.durationMs));
        return f.manualPlays > max ? { ...f, manualPlays: max } : f;
      });
      return clamped.some((f, i) => f !== prev[i]) ? clamped : prev;
    });
  }, [smartTotalMs]);

  const openSmart = () => {
    const seen = new Set<number>(); const pre: SmartFile[] = [];
    for (const it of items) { if (seen.has(it.mediaFileId)) continue; seen.add(it.mediaFileId); pre.push({ name: it.fileName, durationMs: parseDurationMs(it.durationMs), manualPlays: 0, video: isVid(it.mimeType), mediaFileId: it.mediaFileId }); }
    setSmartFiles(pre); setView('smart');
  };

  const addSmartFromMedia = (file: BrowserFile) => {
    if (smartFiles.find(f => f.mediaFileId === file.id)) {
      setSmartDupWarn(file.original_name);
      setTimeout(() => setSmartDupWarn(null), 2000);
      return;
    }
    setSmartFiles(prev => [...prev, { name: file.original_name, durationMs: parseDurationMs(file.duration_ms), manualPlays: 0, video: isVid(file.mime_type), mediaFileId: file.id }]);
  };

  const onSmartZoneDragOver = (e: React.DragEvent) => { if (draggedMediaFile.current.length > 0) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setDropZoneActive(true); } };
  const onSmartZoneDrop = (e: React.DragEvent) => { e.preventDefault(); setDropZoneActive(false); if (draggedMediaFile.current.length > 0) { draggedMediaFile.current.forEach(addSmartFromMedia); draggedMediaFile.current = []; } };

  // Slider drag: clamp to available time
  const sSetManual = (i: number, v: number) => setSmartFiles(prev => {
    const otherMs = prev.reduce((s, f, k) => k !== i && f.manualPlays > 0 ? s + f.manualPlays * f.durationMs : s, 0);
    const max = Math.floor(Math.max(0, smartTotalMs - otherMs) / Math.max(1000, prev[i].durationMs));
    return prev.map((f, j) => j !== i ? f : { ...f, manualPlays: Math.max(0, Math.min(max, v)) });
  });
  // Toggle auto/manual: no time clamping — let user decide the value after
  const sToggleAuto = (i: number) => setSmartFiles(prev => prev.map((f, j) => {
    if (j !== i) return f;
    if (f.manualPlays > 0) return { ...f, manualPlays: 0 };           // → auto
    const autoRounds = (() => {
      const manMs = prev.filter((x, k) => k !== i && x.manualPlays > 0).reduce((s, x) => s + x.manualPlays * x.durationMs, 0);
      const autoFs = prev.filter((x, k) => k !== i && x.manualPlays === 0);
      const autoMs = autoFs.reduce((s, x) => s + x.durationMs, 0) + f.durationMs;
      return autoMs > 0 ? Math.floor(Math.max(0, smartTotalMs - manMs) / autoMs) : 1;
    })();
    return { ...f, manualPlays: Math.max(1, autoRounds) };             // → manual
  }));
  const sRemove = (i: number) => setSmartFiles(prev => prev.filter((_,j) => j !== i));

  const doGenerate = () => {
    type Entry = { item: PlaylistItem; remaining: number; fileId: number };
    const pool: Entry[] = smartFiles
      .map(sf => ({ sf, n: smartCalcN(sf) }))
      .filter(({ n }) => n > 0)
      .map(({ sf, n }) => ({
        item: { mediaFileId: sf.mediaFileId ?? 0, orderIndex: 0, durationOverrideMs: sf.video ? null : sf.durationMs, scaleMode: defaultScale, fileName: sf.name, mimeType: sf.video ? 'video/mp4' : 'image/png', durationMs: sf.durationMs },
        remaining: n,
        fileId: sf.mediaFileId ?? 0,
      }));

    // Greedy even-spread: always pick highest-remaining that differs from last placed.
    // Falls back to same file only when no other option exists.
    const result: PlaylistItem[] = [];
    const entries = pool.map(e => ({ ...e }));
    while (entries.some(e => e.remaining > 0)) {
      entries.sort((a, b) => b.remaining - a.remaining);
      const lastId = result.length > 0 ? result[result.length - 1].mediaFileId : -1;
      let idx = entries.findIndex(e => e.remaining > 0 && e.fileId !== lastId);
      if (idx === -1) idx = entries.findIndex(e => e.remaining > 0);
      if (idx === -1) break;
      result.push({ ...entries[idx].item });
      entries[idx].remaining--;
    }

    setItems(result.map((it, i) => ({ ...it, orderIndex: i })));
    setView('editor'); setSaved(false);
  };


  // Сервер отвечает 409 с перечнем того, что использует объект
  const describeBlock = (err: any): string | null => {
    const body = err?.response?.data;
    if (err?.response?.status !== 409 || body?.code !== 'IN_USE') return null;
    const u = body.usages ?? {};
    const lines: string[] = [];
    for (const s of u.screens ?? []) lines.push(`\u2022 ${s.displayName || s.deviceId}`);
    for (const sc of u.schedules ?? []) lines.push(`\u2022 ${sc.scheduleName} \u2014 ${sc.eventName}`);
    return lines.join('\n') || 'In use';
  };

  const handleDelete = async (id: number) => {
    try {
      await axios.delete(`/playlists/${id}`);
      setCards(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      setBlockMsg(describeBlock(err));
    }
  };

  const handleDuplicate = async (e: React.MouseEvent, pl: PlaylistCard) => {
    e.stopPropagation();
    try {
      const { data } = await axios.post<PlaylistCard>(`/playlists/${pl.id}/duplicate`, { name: `Copy of ${pl.name}` });
      setCards(prev => [data, ...prev]);
    } catch {}
  };

  // ── Small icon action button ──────────────────────────────────────────────────
  const ActionBtn = ({ onClick, title, children, danger, active }: { onClick?: () => void; title?: string; children: React.ReactNode; danger?: boolean; active?: boolean }) => (
    <Box component="button" onClick={onClick} title={title}
      sx={{ width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '5px', border: `1px solid ${active ? 'rgba(99,102,241,.3)' : 'var(--glass-border)'}`, background: active ? 'rgba(99,102,241,.15)' : 'var(--glass-bg)', color: active ? 'var(--accent-light)' : danger ? 'var(--text-disabled)' : 'var(--text-disabled)', cursor: 'pointer', flexShrink: 0, transition: 'all .12s', '&:hover': { background: danger ? 'rgba(248,113,113,.12)' : active ? 'rgba(99,102,241,.2)' : 'var(--glass-hover)', color: danger ? '#f87171' : active ? 'var(--accent-light)' : 'var(--text-secondary)', borderColor: danger ? 'rgba(248,113,113,.3)' : active ? 'rgba(99,102,241,.4)' : 'var(--glass-border)' } }}>
      {children}
    </Box>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // VIEW: CARDS
  // ═══════════════════════════════════════════════════════════════════════════
  if (view === 'cards') return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', p: { xs: '12px', md: '20px' }, gap: '16px' }}>

      {/* ── Toolbar — hidden on mobile: the count only ate vertical space and the
             create button now lives in the topbar ── */}
      <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: '10px', flexShrink: 0 }}>
        <Typography sx={{ fontSize: 13, color: 'var(--text-disabled)' }}>{cards.length} playlists</Typography>
        <Box sx={{ flex: 1 }} />

        {/* Grid / List toggle — desktop only */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, border: '1px solid var(--glass-border)', borderRadius: '8px', overflow: 'hidden' }}>
          {[false, true].map(isListMode => (
            <Box
              key={String(isListMode)}
              component="button"
              onClick={() => setListView(isListMode)}
              title={isListMode ? 'List view' : 'Grid view'}
              sx={{
                width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: 'none', cursor: 'pointer', fontFamily: 'inherit', transition: 'all .12s',
                bgcolor: listView === isListMode ? 'var(--selected-bg)' : 'transparent',
                color: listView === isListMode ? 'var(--text-primary)' : 'var(--text-disabled)',
                '&:hover': { color: 'var(--text-primary)' },
              }}
            >
              {isListMode ? (
                <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
              ) : (
                <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
              )}
            </Box>
          ))}
        </Box>

        <Box component="button" onClick={handleCreate} disabled={creating}
          sx={{ ...btnPrimarySx, ...(creating ? btnDisabledSx : {}), display: { xs: 'none', md: 'inline-flex' } }}>
          <IcoPlus /> New Playlist
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', '&::-webkit-scrollbar': { width: 5 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,.08)', borderRadius: 4 }, ...scrollAreaSx }}>
        {cardsLoading ? (
          <Typography sx={{ fontSize: 13, color: 'var(--text-disabled)', py: '40px', textAlign: 'center' }}>Loading…</Typography>
        ) : listView && !isMobile ? (
          /* ── List view ── */
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {cards.map(pl => (
              <Box key={pl.id} onClick={() => { if (editingCardId === pl.id) return; navigate(`/playlists/${pl.id}`); }}
                sx={{ display: 'flex', alignItems: 'center', gap: '12px', px: '14px', py: '10px', borderRadius: '10px', cursor: editingCardId === pl.id ? 'default' : 'pointer', ...glass, border: '1px solid var(--glass-border)', transition: 'border-color .12s, background .12s', '&:hover': { borderColor: 'rgba(99,102,241,.3)', bgcolor: 'var(--glass-hover)' }, '&:hover .pl-rename-btn': { opacity: 1 } }}>
                {/* Icon */}
                <Box sx={{ width: 34, height: 34, borderRadius: '8px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(99,102,241,.1)', border: '1px solid rgba(99,102,241,.18)', color: '#a5b4fc' }}>
                  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><circle cx="3.5" cy="6" r="1.5" fill="currentColor"/><circle cx="3.5" cy="12" r="1.5" fill="currentColor"/><circle cx="3.5" cy="18" r="1.5" fill="currentColor"/></svg>
                </Box>
                {/* Name */}
                {editingCardId === pl.id ? (
                  <Box component="input" autoFocus value={editingCardName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingCardName(e.target.value)}
                    onBlur={() => commitCardRename(pl.id)}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') commitCardRename(pl.id); if (e.key === 'Escape') setEditingCardId(null); }}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    sx={{ flex: 1, minWidth: 0, background: 'var(--input-bg)', border: '1px solid var(--accent)', borderRadius: '5px', outline: 'none', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'inherit', px: '6px', py: '3px' }} />
                ) : (
                  <>
                    <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pl.name}</Typography>
                    <Box component="button" className="pl-rename-btn" onClick={e => startCardRename(e, pl.id, pl.name)}
                      title="Rename" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '4px', background: 'none', border: 'none', color: 'var(--text-disabled)', cursor: 'pointer', opacity: 0, flexShrink: 0, transition: 'opacity .1s', fontSize: 12, '&:hover': { color: 'var(--accent-light)' } }}>✏</Box>
                    <Box component="button" className="pl-rename-btn" onClick={e => handleDuplicate(e, pl)}
                      title={t('playlists.duplicate')} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '4px', background: 'none', border: 'none', color: 'var(--text-disabled)', cursor: 'pointer', opacity: 0, flexShrink: 0, transition: 'opacity .1s', '&:hover': { color: 'var(--accent-light)' } }}>
                      <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                    </Box>
                  </>
                )}
                {/* Files badge */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', px: '8px', py: '2px', borderRadius: '6px', bgcolor: 'rgba(99,102,241,.1)', border: '1px solid rgba(99,102,241,.18)' }}>
                  <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="#a5b4fc" strokeWidth={2}><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><circle cx="3.5" cy="6" r="1.5" fill="#a5b4fc"/><circle cx="3.5" cy="12" r="1.5" fill="#a5b4fc"/></svg>
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#a5b4fc' }}>{pl.itemCount}</Typography>
                </Box>
                {/* Duration */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--text-disabled)', minWidth: 50 }}>
                  <IcoClock /><Typography sx={{ fontSize: 11 }}>{fmtMs(pl.totalDurationMs)}</Typography>
                </Box>
                {/* Date */}
                <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', minWidth: 60, textAlign: 'right', flexShrink: 0 }}>{fmtDate(pl.updatedAt)}</Typography>
                {/* Delete */}
                <Box component="button" onClick={async e => { e.stopPropagation(); if (await confirm({ title: t('playlists.deleteTitle'), message: t('playlists.deleteHint'), confirmLabel: t('common.delete'), cancelLabel: t('common.cancel'), variant: 'danger' })) handleDelete(pl.id); }}
                  sx={{ display: 'flex', width: 26, height: 26, alignItems: 'center', justifyContent: 'center', borderRadius: '6px', background: 'none', border: 'none', color: 'var(--text-disabled)', cursor: 'pointer', opacity: .4, flexShrink: 0, transition: 'all .1s', '&:hover': { opacity: 1, color: '#f87171' } }}><IcoX /></Box>
              </Box>
            ))}
          </Box>
        ) : (
          /* ── Grid view ── */
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
            {cards.map(pl => (
              <Box key={pl.id} onClick={() => { if (editingCardId === pl.id) return; navigate(`/playlists/${pl.id}`); }}
                sx={{ borderRadius: '14px', cursor: editingCardId === pl.id ? 'default' : 'pointer', ...glass, border: '1px solid var(--glass-border)', display: 'flex', flexDirection: 'column', gap: '8px', p: '12px', height: 120, transition: 'border-color .15s', '&:hover': { borderColor: 'rgba(99,102,241,.5)' }, '&:hover .pl-rename-btn': { opacity: 1 } }}>

                {/* Row 1: name + ✏ + ✕ inline */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
                  {editingCardId === pl.id ? (
                    <Box component="input" autoFocus value={editingCardName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingCardName(e.target.value)}
                      onBlur={() => commitCardRename(pl.id)}
                      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') commitCardRename(pl.id); if (e.key === 'Escape') setEditingCardId(null); }}
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      sx={{ flex: 1, minWidth: 0, background: 'var(--input-bg)', border: '1px solid var(--accent)', borderRadius: '5px', outline: 'none', fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'inherit', px: '6px', py: '3px' }} />
                  ) : (
                    <Typography sx={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pl.name}</Typography>
                  )}
                  <Box component="button" className="pl-rename-btn" onClick={e => startCardRename(e, pl.id, pl.name)}
                    title="Rename" sx={{ flexShrink: 0, width: 24, height: 24, borderRadius: '6px', border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: 'var(--text-disabled)', opacity: 0, transition: 'all .15s', '&:hover': { color: 'var(--accent-light)', bgcolor: 'rgba(99,102,241,.12)', opacity: 1 } }}>✏</Box>
                  <Box component="button" className="pl-rename-btn" onClick={e => handleDuplicate(e, pl)}
                    title={t('playlists.duplicate')} sx={{ flexShrink: 0, width: 24, height: 24, borderRadius: '6px', border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-disabled)', opacity: 0, transition: 'all .15s', '&:hover': { color: 'var(--accent-light)', bgcolor: 'rgba(99,102,241,.12)', opacity: 1 } }}>
                    <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                  </Box>
                  <Box component="button" onClick={async e => { e.stopPropagation(); if (await confirm({ title: t('playlists.deleteTitle'), message: t('playlists.deleteHint'), confirmLabel: t('common.delete'), cancelLabel: t('common.cancel'), variant: 'danger' })) handleDelete(pl.id); }}
                    sx={{ flexShrink: 0, width: 26, height: 26, borderRadius: '7px', border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: 'var(--text-disabled)', opacity: .5, transition: 'all .15s', '&:hover': { color: '#f87171', bgcolor: 'rgba(248,113,113,.12)', opacity: 1 } }}>
                    <IcoX />
                  </Box>
                </Box>

                {/* Row 2: files count + duration */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="#a5b4fc" strokeWidth={2}><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><circle cx="3.5" cy="6" r="1.5" fill="#a5b4fc"/><circle cx="3.5" cy="12" r="1.5" fill="#a5b4fc"/></svg>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#a5b4fc' }}>{pl.itemCount} files</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#a5b4fc', fontVariantNumeric: 'tabular-nums' }}>
                    {pl.totalDurationMs > 0 ? fmtMs(pl.totalDurationMs) : '—:——'}
                  </Typography>
                </Box>

                {/* Row 3: video + image counts */}
                <Box sx={{ display: 'flex', gap: '14px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Typography sx={{ fontSize: 11 }}>🎬</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--text-disabled)' }}>{pl.videoCount} video</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Typography sx={{ fontSize: 11 }}>🖼</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--text-disabled)' }}>{pl.imageCount} image</Typography>
                  </Box>
                </Box>
              </Box>
            ))}
            {/* New playlist tile */}
            <Box onClick={handleCreate} sx={{ borderRadius: '14px', border: '1.5px dashed rgba(99,102,241,.2)', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', minHeight: 140, color: 'rgba(99,102,241,.5)', transition: 'all .15s', '&:hover': { borderColor: 'rgba(99,102,241,.5)', background: 'rgba(99,102,241,.04)', color: 'rgba(99,102,241,.8)' } }}>
              <Box sx={{ fontSize: 22 }}>+</Box><Typography sx={{ fontSize: 13, fontWeight: 600 }}>New Playlist</Typography>
            </Box>
          </Box>
        )}
      </Box>

      {blockMsg && (
        <Box onClick={() => setBlockMsg(null)} sx={{
          position: 'fixed', inset: 0, zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,.6)', backdropFilter: 'blur(6px)',
        }}>
          <Box onClick={e => e.stopPropagation()} sx={{
            width: 380, maxWidth: '90vw', background: 'var(--glass-bg)', backdropFilter: 'blur(24px)',
            border: '1px solid var(--glass-border)', borderRadius: '16px', overflow: 'hidden',
          }}>
            <Box sx={{ px: '20px', py: '14px', borderBottom: '1px solid var(--glass-border)', fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>
              Playlist is in use
            </Box>
            <Box sx={{ px: '20px', py: '16px', fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'pre-line' }}>
              {blockMsg}
            </Box>
            <Box sx={{ px: '20px', py: '14px', borderTop: '1px solid var(--glass-border)', display: 'flex', justifyContent: 'flex-end' }}>
              <Box component="button" onClick={() => setBlockMsg(null)} sx={{
                px: '12px', py: '6px', borderRadius: '8px', background: 'var(--glass-bg)',
                border: '1px solid var(--glass-border)', color: 'var(--text-secondary)',
                fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
              }}>Close</Box>
            </Box>
          </Box>
        </Box>
      )}
      {confirmModal}
    </Box>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // VIEW: EDITOR
  // ═══════════════════════════════════════════════════════════════════════════
  if (view === 'editor') return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ flex: 1, display: 'flex', gap: '10px', overflow: 'hidden', p: { xs: 0, md: '8px 14px 14px' } }}>

        {/* LEFT: playlist */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', ...glass, borderRadius: { xs: 0, md: '14px' }, border: { xs: 'none', md: '1px solid var(--glass-border)' }, outline: dropZoneActive ? '2px solid var(--accent)' : '2px solid transparent', transition: 'outline-color .15s' }}
          onDragOver={onPlaylistZoneDragOver} onDrop={onPlaylistZoneDrop} onDragLeave={() => setDropZoneActive(false)}>

          {/* Header */}
          <Box sx={{ flexShrink: 0, borderBottom: `1px solid ${settingsOpen ? 'transparent' : 'var(--divider)'}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', px: '12px', py: '8px', background: 'var(--glass-hover)' }}>
              <Box component="input" value={editorName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setEditorName(e.target.value); setSaved(false); }}
                sx={{ flex: 1, minWidth: 0, background: 'none', border: 'none', outline: 'none', fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'inherit', borderBottom: '1px solid transparent', transition: 'border-color .12s', px: '2px', '&:focus': { borderBottomColor: 'var(--accent)' } }} />
              {/* Settings toggle */}
              <Box component="button" onClick={() => setSettingsOpen(v => !v)}
                sx={{ display: 'flex', alignItems: 'center', gap: '4px', px: '9px', py: '4px', borderRadius: '7px', background: settingsOpen ? 'rgba(99,102,241,.15)' : 'var(--glass-bg)', border: `1px solid ${settingsOpen ? 'rgba(99,102,241,.3)' : 'var(--glass-border)'}`, color: settingsOpen ? 'var(--accent-light)' : 'var(--text-disabled)', fontSize: 11, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', flexShrink: 0, transition: 'all .15s', '&:hover': { color: 'var(--accent-light)', borderColor: 'rgba(99,102,241,.3)', background: 'rgba(99,102,241,.1)' } }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                Settings
              </Box>
              <Box sx={{ width: '1px', height: 16, background: 'var(--divider)', flexShrink: 0 }} />
              <Box component="button" onClick={openSmart}
                sx={{ display: 'flex', alignItems: 'center', gap: '4px', px: '9px', py: '4px', borderRadius: '7px', background: 'linear-gradient(135deg,rgba(168,85,247,.8),rgba(192,38,211,.7))', border: 'none', color: '#fff', fontSize: 11, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', flexShrink: 0, boxShadow: '0 2px 6px rgba(168,85,247,.25)', '&:hover': { opacity: .88 } }}>
                <IcoStar /> Build Playlist
              </Box>
              <Box component="button" onClick={handleSave} disabled={saving}
                sx={{ display: 'flex', alignItems: 'center', gap: '4px', px: '10px', py: '4px', borderRadius: '7px', flexShrink: 0, background: saved ? 'rgba(52,211,153,.12)' : 'var(--accent)', border: saved ? '1px solid rgba(52,211,153,.3)' : 'none', color: saved ? '#34d399' : '#fff', fontSize: 11, fontWeight: 600, fontFamily: 'inherit', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1, transition: 'all .2s' }}>
                {saved ? '✓ Saved' : saving ? '…' : 'Save'}
              </Box>
            </Box>
            {settingsOpen && (
              <Box sx={{ px: '14px', py: '10px', borderTop: '1px solid var(--divider)', borderBottom: '1px solid var(--divider)', background: 'var(--glass-hover)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'var(--text-disabled)', minWidth: 110 }}>Default scale</Typography>
                  <Box sx={{ display: 'flex', gap: '4px' }}>
                    {(['fill','fit','stretch'] as const).map(mode => (
                      <Box key={mode} component="button" onClick={() => setDefaultScale(mode)}
                        sx={{ px: '8px', py: '3px', borderRadius: '6px', fontSize: 11, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', transition: 'all .1s', border: `1px solid ${defaultScale===mode?'rgba(99,102,241,.35)':'var(--glass-border)'}`, background: defaultScale===mode?'rgba(99,102,241,.18)':'var(--glass-bg)', color: defaultScale===mode?'var(--accent-light)':'var(--text-secondary)' }}>{mode}</Box>
                    ))}
                  </Box>
                  <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', ml: 'auto' }}>Applied to newly added images</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'var(--text-disabled)', minWidth: 110 }}>Default image dur.</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {[-5,-1,+1,+5].map(d => (
                      <Box key={d} component="button" onClick={() => setDefaultImgDurSec(v => Math.max(1, v + d))}
                        sx={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '5px', border: '1px solid var(--glass-border)', background: 'var(--glass-bg)', color: 'var(--text-secondary)', fontSize: 10, fontFamily: 'monospace', cursor: 'pointer', fontWeight: 700, '&:hover': { background: 'rgba(99,102,241,.15)', borderColor: 'rgba(99,102,241,.35)', color: 'var(--accent-light)' } }}>
                        {d>0?`+${d}`:d}
                      </Box>
                    ))}
                    <Box component="input" type="number" min={1} max={3600} value={defaultImgDurSec} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDefaultImgDurSec(Math.max(1, +e.target.value))}
                      sx={{ width: 48, px: '6px', py: '3px', borderRadius: '6px', fontSize: 12, fontWeight: 700, fontFamily: 'monospace', background: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--text-primary)', textAlign: 'center', outline: 'none', '&:focus': { borderColor: 'var(--accent)' } }} />
                    <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>sec</Typography>
                  </Box>
                </Box>

                {/* Divider */}
                <Box sx={{ height: '1px', background: 'var(--divider)', mx: '-14px' }} />

                {/* Description */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'var(--text-disabled)', minWidth: 110, pt: '4px' }}>Description</Typography>
                  <Box component="textarea" value={description} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)} rows={2} placeholder="Playlist description…"
                    sx={{ flex: 1, background: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: 12, p: '5px 8px', outline: 'none', resize: 'vertical', fontFamily: 'inherit', '&::placeholder': { color: 'var(--text-disabled)' }, '&:focus': { borderColor: 'var(--accent)' } }} />
                </Box>

                {/* Transition */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'var(--text-disabled)', minWidth: 110 }}>Transition</Typography>
                  <Box sx={{ display: 'flex', gap: '4px' }}>
                    {(['cut','fade','slide'] as const).map(type => (
                      <Box key={type} component="button" onClick={() => { setTransitionType(type); setSaved(false); }}
                        sx={{ px: '8px', py: '3px', borderRadius: '6px', fontSize: 11, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', transition: 'all .1s', border: `1px solid ${transitionType===type?'rgba(99,102,241,.35)':'var(--glass-border)'}`, background: transitionType===type?'rgba(99,102,241,.18)':'var(--glass-bg)', color: transitionType===type?'var(--accent-light)':'var(--text-secondary)' }}>{type}</Box>
                    ))}
                  </Box>
                  {transitionType !== 'cut' && (
                    <>
                      <Box component="input" type="number" min={100} max={3000} step={100} value={transitionDurationMs}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setTransitionDurationMs(+e.target.value); setSaved(false); }}
                        sx={{ width: 56, px: '6px', py: '3px', borderRadius: '6px', fontSize: 12, fontWeight: 700, fontFamily: 'monospace', background: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--text-primary)', textAlign: 'center', outline: 'none', '&:focus': { borderColor: 'var(--accent)' } }} />
                      <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>ms</Typography>
                    </>
                  )}
                </Box>

                {/* Playback toggles */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'var(--text-disabled)', minWidth: 110, flexShrink: 0 }}>Playback</Typography>
                  {([
                    { label: 'No black gap', value: noBlackGap, set: setNoBlackGap },
                    { label: 'Random order', value: randomPlayback, set: setRandomPlayback },
                    { label: 'Sync', value: syncEnabled, set: setSyncEnabled },
                  ] as const).map(opt => (
                    <Box key={opt.label} onClick={() => { opt.set(!opt.value as any); setSaved(false); }}
                      sx={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                      <Box sx={{ width: 28, height: 16, borderRadius: '8px', flexShrink: 0, background: opt.value ? 'var(--accent)' : 'rgba(255,255,255,.08)', border: `1px solid ${opt.value ? 'var(--accent)' : 'var(--glass-border)'}`, position: 'relative', transition: 'background .15s, border-color .15s' }}>
                        <Box sx={{ position: 'absolute', top: 2, left: opt.value ? 13 : 2, width: 10, height: 10, borderRadius: '50%', background: '#fff', transition: 'left .15s' }} />
                      </Box>
                      <Typography sx={{ fontSize: 11, color: 'var(--text-secondary)', userSelect: 'none', whiteSpace: 'nowrap' }}>{opt.label}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          {/* Column header */}
          {items.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', px: '10px', py: '5px', borderBottom: '1px solid var(--divider)', background: 'rgba(0,0,0,.06)', flexShrink: 0 }}>
              {/* Select-all checkbox */}
              <Box onClick={() => selectedItems.size === items.length ? clearSelection() : selectAll()}
                sx={{ width: 14, height: 14, borderRadius: '3px', flexShrink: 0, cursor: 'pointer', border: `1.5px solid ${selectedItems.size > 0 ? 'var(--accent)' : 'var(--glass-border-strong)'}`, background: selectedItems.size === items.length && items.length > 0 ? 'var(--accent)' : selectedItems.size > 0 ? 'rgba(99,102,241,.3)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .1s', '&:hover': { borderColor: 'var(--accent)' } }}>
                {selectedItems.size > 0 && selectedItems.size < items.length && <Box sx={{ width: 6, height: 1.5, background: 'white', borderRadius: 1 }} />}
                {selectedItems.size === items.length && items.length > 0 && <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
              </Box>
              {[{ label: '#', w: 46 }, { label: '', w: 64 }, { label: 'Name', w: 'auto' }].map(({ label, w }, i) => (
                <Typography key={i} sx={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--text-disabled)', width: w === 'auto' ? undefined : w, flex: w === 'auto' ? 1 : undefined, minWidth: 0 }}>{label}</Typography>
              ))}
            </Box>
          )}


          {/* Item list */}
          <Box ref={listRef} sx={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', px: '6px', py: '6px', pb: { xs: '18px', md: '6px' }, display: 'flex', flexDirection: 'column', gap: '3px', '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,.08)', borderRadius: 4 } }}>
            {itemsLoading ? (
              <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)', py: '20px', textAlign: 'center' }}>Loading…</Typography>
            ) : items.length === 0 ? (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', border: '1.5px dashed rgba(99,102,241,.18)', borderRadius: '10px', color: 'rgba(99,102,241,.5)', m: '8px' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'rgba(99,102,241,.65)' }}>Playlist is empty</Typography>
                <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>Drag files from the library or double-click them</Typography>
              </Box>
            ) : items.map((item, idx) => {
              const vid = isVid(item.mimeType); const dur = itemDur(item);
              const gk = item.groupKey;
              const inGroup = gk != null;
              const isGroupStart = inGroup && gk !== items[idx - 1]?.groupKey;
              const isGroupEnd   = inGroup && gk !== items[idx + 1]?.groupKey;
              const groupSize = inGroup ? items.filter(i => i.groupKey === gk).length : 0;
              const rot = inGroup ? items.slice(0, idx).filter(i => i.groupKey === gk).length : 0;
              return (
                <React.Fragment key={`${item.mediaFileId}-${idx}`}>
                {isGroupStart && (() => {
                  const groupAllSelected = Array.from({length: groupSize}, (_, i) => idx + i).every(i => selectedItems.has(i));
                  const toggleGroupSelect = (e: React.MouseEvent) => {
                    e.stopPropagation();
                    setSelectedItems(prev => {
                      const n = new Set(prev);
                      if (groupAllSelected) { for (let i = idx; i < idx + groupSize; i++) n.delete(i); }
                      else                  { for (let i = idx; i < idx + groupSize; i++) n.add(i); }
                      return n;
                    });
                  };
                  const groupStart = idx;
                  const groupEnd = idx + groupSize - 1;
                  const isCollapsedGroup = collapsedGroups.has(gk!);
                  const toggleCollapse = (e: React.MouseEvent) => {
                    e.stopPropagation();
                    setCollapsedGroups(prev => { const n = new Set(prev); n.has(gk!) ? n.delete(gk!) : n.add(gk!); return n; });
                  };
                  const groupDurMs = Array.from({length: groupSize}, (_, i) => items[idx + i]).reduce((s, it) => s + itemDur(it), 0);
                  return (
                  <Box
                    data-item-idx={groupStart}
                    draggable={!isTouch}
                    onDragStart={e => onItemDragStart(e, groupStart, true)}
                    onDragOver={e => { e.preventDefault(); if (isCollapsedGroup) onItemDragOver(e, groupStart); }}
                    onDragLeave={e => { if (isCollapsedGroup && !(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) setDropTarget(null); }}
                    onDrop={e => {
                      if (isCollapsedGroup) {
                        stopScrollRaf();
                        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                        const before = e.clientY < rect.top + rect.height / 2;
                        setDropTarget(null);
                        if (before) handleGapDrop(e, groupStart - 1);
                        else        handleGapDrop(e, groupEnd);
                      }
                    }}
                    onDragEnd={stopScrollRaf}
                    onTouchStart={e => onItemTouchStart(e, groupStart, true)}
                    onTouchMove={onItemTouchMove}
                    onTouchEnd={cancelPress}
                    onClick={toggleCollapse}
                    sx={{ display: 'flex', alignItems: 'center', gap: '6px', px: '8px', py: '6px',
                    ...(touchDrag?.from === groupStart ? { opacity: .55 } : {}),
                    ...(touchDrag ? { touchAction: 'none' } : {}),
                    borderRadius: isCollapsedGroup ? '9px' : '9px 9px 0 0',
                    border: '1px solid rgba(217,119,6,.35)',
                    borderBottom: isCollapsedGroup ? '1px solid rgba(217,119,6,.35)' : 'none',
                    background: 'rgba(217,119,6,.07)', cursor: 'pointer', position: 'relative', '&:hover': { background: 'rgba(217,119,6,.1)' } }}>

                    {/* Drop indicators for collapsed group (before/after) */}
                    {isCollapsedGroup && dropTarget?.idx === groupStart && dropTarget.pos === 'before' && (
                      <Box sx={{ position: 'absolute', top: -2, left: 8, right: 8, height: 2, borderRadius: 1, background: 'var(--accent)', boxShadow: '0 0 8px var(--accent)', zIndex: 10, pointerEvents: 'none' }} />
                    )}
                    {isCollapsedGroup && dropTarget?.idx === groupStart && dropTarget.pos === 'after' && (
                      <Box sx={{ position: 'absolute', bottom: -2, left: 8, right: 8, height: 2, borderRadius: 1, background: 'var(--accent)', boxShadow: '0 0 8px var(--accent)', zIndex: 10, pointerEvents: 'none' }} />
                    )}

                    {/* Checkbox */}
                    <Box onClick={e => { e.stopPropagation(); toggleGroupSelect(e); }}
                      sx={{ width: 14, height: 14, borderRadius: '3px', flexShrink: 0, cursor: 'pointer',
                        border: `1.5px solid ${groupAllSelected ? '#d97706' : 'var(--glass-border-strong)'}`,
                        background: groupAllSelected ? 'rgba(217,119,6,.3)' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .1s',
                        '&:hover': { borderColor: '#d97706' } }}>
                      {groupAllSelected && <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                    </Box>

                    {/* Up/Down arrows + slot number */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px', flexShrink: 0 }}>
                      <Box component="button" onClick={e => { e.stopPropagation(); moveItem(groupStart, -1, true); }}
                        sx={{ width: 28, height: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '3px', border: '1px solid rgba(217,119,6,.3)', background: 'transparent', color: 'rgba(217,119,6,.5)', cursor: 'pointer', flexShrink: 0, transition: 'all .1s', '&:hover': { background: 'rgba(217,119,6,.1)', color: '#d97706', borderColor: 'rgba(217,119,6,.6)' } }}>
                        <IcoUp />
                      </Box>
                      <Box sx={{ width: 28, height: 20, borderRadius: '5px', background: 'rgba(217,119,6,.18)', border: '1px solid rgba(217,119,6,.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#d97706', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{slotNumbers[idx]}</Typography>
                      </Box>
                      <Box component="button" onClick={e => { e.stopPropagation(); moveItem(groupEnd, 1, true); }}
                        sx={{ width: 28, height: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '3px', border: '1px solid rgba(217,119,6,.3)', background: 'transparent', color: 'rgba(217,119,6,.5)', cursor: 'pointer', flexShrink: 0, transition: 'all .1s', '&:hover': { background: 'rgba(217,119,6,.1)', color: '#d97706', borderColor: 'rgba(217,119,6,.6)' } }}>
                        <IcoDown />
                      </Box>
                    </Box>

                    <Box sx={{ width: 52, height: 52, borderRadius: '8px', border: '1px dashed rgba(217,119,6,.4)', background: 'rgba(217,119,6,.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>🗂</Box>

                    {/* Group name: display or edit mode */}
                    {editingGroupKey === gk ? (
                      <Box
                        component="input"
                        autoFocus
                        value={item.groupName ?? ''}
                        placeholder={t('playlists2.groupPlaceholder')}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => renameGroup(gk!, e.target.value)}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        onBlur={() => setEditingGroupKey(null)}
                        onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === 'Escape') setEditingGroupKey(null); }}
                        sx={{ flex: 1, minWidth: 0, px: '6px', py: '3px', borderRadius: '6px', background: 'var(--input-bg)', border: '1px solid rgba(217,119,6,.5)', color: 'var(--text-primary)', fontSize: 12.5, fontWeight: 700, fontFamily: 'inherit', outline: 'none' }} />
                    ) : (
                      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '4px',
                        '&:hover .grp-pencil': { opacity: 1 } }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1 }}>
                          {item.groupName || t('playlists2.groupPlaceholder')}
                        </Typography>
                        <Box className="grp-pencil" onClick={e => { e.stopPropagation(); setEditingGroupKey(gk!); }}
                          sx={{ opacity: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: '4px', border: '1px solid rgba(217,119,6,.3)', color: 'rgba(217,119,6,.7)', cursor: 'pointer', flexShrink: 0, transition: 'opacity .15s', '&:hover': { background: 'rgba(217,119,6,.12)', color: '#d97706', borderColor: 'rgba(217,119,6,.5)' } }}>
                          <IcoPencil />
                        </Box>
                      </Box>
                    )}

                    <Typography sx={{ fontSize: 10, fontWeight: 700, px: '7px', py: '2px', borderRadius: '20px', background: 'rgba(217,119,6,.14)', color: '#d97706', border: '1px solid rgba(217,119,6,.25)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {t('playlists2.inRotation', { count: groupSize })}
                    </Typography>
                    <Box component="button" onClick={e => { e.stopPropagation(); ungroup(gk!); }} title={t('playlists2.ungroup')}
                      sx={{ px: '8px', py: '3px', borderRadius: '6px', border: '1px solid var(--glass-border)', background: 'var(--glass-bg)', color: 'var(--text-secondary)', fontSize: 10, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', flexShrink: 0, '&:hover': { color: 'var(--text-primary)', background: 'var(--glass-hover)' } }}>
                      {t('playlists2.ungroup')}
                    </Box>
                    {/* Collapse toggle */}
                    <Box onClick={e => { e.stopPropagation(); toggleCollapse(e); }}
                      sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '5px', flexShrink: 0, cursor: 'pointer', color: 'rgba(217,119,6,.6)', border: '1px solid rgba(217,119,6,.2)', '&:hover': { color: '#d97706', background: 'rgba(217,119,6,.12)', borderColor: 'rgba(217,119,6,.4)' }, transition: 'all .1s' }}>
                      {isCollapsedGroup ? <IcoChevRight /> : <IcoChevDown />}
                    </Box>
                    {/* Group total duration */}
                    <Box onClick={e => e.stopPropagation()} sx={{ px: '7px', py: '3px', borderRadius: '6px', background: 'rgba(217,119,6,.08)', border: '1px solid rgba(217,119,6,.2)', flexShrink: 0 }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#d97706', fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{fmtMs(groupDurMs)}</Typography>
                    </Box>
                  </Box>
                  );
                })()}
                {!(inGroup && collapsedGroups.has(gk!)) && <Box
                  data-item-idx={idx}
                  draggable={!isTouch}
                  onDragStart={e => onItemDragStart(e, idx)}
                  onDragOver={e => onItemDragOver(e, idx)}
                  onDragLeave={e => { if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) setDropTarget(null); }}
                  onDrop={e => onItemDrop(e, idx)}
                  onDragEnd={stopScrollRaf}
                  onTouchStart={e => onItemTouchStart(e, idx)}
                  onTouchMove={onItemTouchMove}
                  onTouchEnd={cancelPress}
                  sx={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'visible', cursor: 'grab', transition: 'background .2s, opacity .12s', px: '8px', py: '5px', position: 'relative',
                    ...(touchDrag?.from === idx ? { opacity: .55, boxShadow: '0 6px 18px rgba(0,0,0,.35)' } : {}),
                    ...(touchDrag ? { touchAction: 'none' } : {}),
                    ...(inGroup ? {
                      mt: '-3px',
                      borderRadius: isGroupEnd ? '0 0 9px 9px' : 0,
                      border: '1px solid rgba(217,119,6,.35)',
                      borderTop: 'none',
                      borderBottom: isGroupEnd ? '1px solid rgba(217,119,6,.35)' : 'none',
                      background: swapping && (swapping[0]===idx||swapping[1]===idx) ? 'rgba(99,102,241,.08)' : selectedItems.has(idx) ? 'rgba(99,102,241,.05)' : 'rgba(217,119,6,.025)',
                      '&:hover': { background: selectedItems.has(idx) ? 'rgba(99,102,241,.08)' : 'rgba(217,119,6,.05)' },
                      pl: '18px',
                      ...(isGroupEnd ? { pb: '4px' } : {}),
                    } : {
                      borderRadius: '9px',
                      border: `1px solid ${swapping && (swapping[0]===idx||swapping[1]===idx) ? 'rgba(99,102,241,.5)' : selectedItems.has(idx) ? 'rgba(99,102,241,.35)' : 'var(--glass-border)'}`,
                      background: swapping && (swapping[0]===idx||swapping[1]===idx) ? 'rgba(99,102,241,.1)' : selectedItems.has(idx) ? 'rgba(99,102,241,.06)' : 'rgba(255,255,255,.02)',
                      '&:hover': { borderColor: selectedItems.has(idx) ? 'rgba(99,102,241,.5)' : 'rgba(99,102,241,.2)', background: selectedItems.has(idx) ? 'rgba(99,102,241,.09)' : 'rgba(255,255,255,.035)' },
                    }) }}>

                  {/* Drop indicators */}
                  {dropTarget?.idx === idx && (() => {
                    const isMediaDrag = draggedMediaFile.current.length > 0;
                    const goesIntoGroup = inGroup;
                    const clr  = goesIntoGroup ? '#d97706' : 'var(--accent)';
                    const glow = goesIntoGroup ? 'rgba(217,119,6,.6)' : 'var(--accent)';
                    return (
                      <>
                        {dropTarget.pos === 'before' && (
                          <Box sx={{ position: 'absolute', top: -2, left: 8, right: 8, height: 2, borderRadius: 1, background: clr, boxShadow: `0 0 8px ${glow}`, zIndex: 10, pointerEvents: 'none' }} />
                        )}
                        {dropTarget.pos === 'after' && (
                          <Box sx={{ position: 'absolute', bottom: inGroup && isGroupEnd ? 2 : -2, left: 8, right: 8, height: 2, borderRadius: 1, background: clr, boxShadow: `0 0 8px ${glow}`, zIndex: 10, pointerEvents: 'none' }} />
                        )}
                      </>
                    );
                  })()}

                  {/* Checkbox */}
                  <Box onClick={e => { e.stopPropagation(); toggleSelect(idx); }} onMouseDown={e => e.stopPropagation()}
                    sx={{ width: 14, height: 14, borderRadius: '3px', flexShrink: 0, cursor: 'pointer', border: `1.5px solid ${selectedItems.has(idx) ? 'var(--accent)' : 'var(--glass-border-strong)'}`, background: selectedItems.has(idx) ? 'var(--accent)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .1s', '&:hover': { borderColor: 'var(--accent)' } }}>
                    {selectedItems.has(idx) && <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                  </Box>

                  {/* Number + arrows column */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px', flexShrink: 0 }} onMouseDown={e => e.stopPropagation()}>
                    <Box component="button" onClick={e => { e.stopPropagation(); moveItem(idx, -1); }}
                      sx={{ width: 28, height: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '3px', border: '1px solid var(--glass-border)', background: 'transparent', color: 'var(--text-disabled)', cursor: 'pointer', flexShrink: 0, transition: 'all .1s', '&:hover': { background: 'rgba(99,102,241,.15)', color: 'var(--accent-light)', borderColor: 'rgba(99,102,241,.35)' } }}>
                      <IcoUp />
                    </Box>
                    <Box sx={{ width: 28, height: 20, borderRadius: '5px', background: vid ? 'rgba(99,102,241,.15)' : 'rgba(16,185,129,.1)', border: `1px solid ${vid ? 'rgba(99,102,241,.2)' : 'rgba(16,185,129,.2)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 800, color: vid ? 'var(--accent-light)' : '#34d399', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{inGroup ? `${slotNumbers[idx]}.${rot + 1}` : slotNumbers[idx]}</Typography>
                    </Box>
                    <Box component="button" onClick={e => { e.stopPropagation(); moveItem(idx, 1); }}
                      sx={{ width: 28, height: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '3px', border: '1px solid var(--glass-border)', background: 'transparent', color: 'var(--text-disabled)', cursor: 'pointer', flexShrink: 0, transition: 'all .1s', '&:hover': { background: 'rgba(99,102,241,.15)', color: 'var(--accent-light)', borderColor: 'rgba(99,102,241,.35)' } }}>
                      <IcoDown />
                    </Box>
                  </Box>

                  {/* Thumbnail */}
                  <Box sx={{ width: 52, height: 52, borderRadius: '8px', overflow: 'hidden', flexShrink: 0, position: 'relative', background: vid ? 'linear-gradient(135deg,#071a30,#0f3d6e)' : 'linear-gradient(135deg,#064e3b,#0e7c5a)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ThumbImg fileId={item.mediaFileId} />
                    <Box sx={{ position: 'absolute', bottom: 2, right: 2, width: 14, height: 14, borderRadius: '3px', background: vid ? 'rgba(99,102,241,.9)' : 'rgba(16,185,129,.9)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {vid ? <IcoVideo /> : <IcoImage />}
                    </Box>
                  </Box>

                  {/* Main info */}
                  <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.2 }}>{item.fileName}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                      {item.width && item.height && <Box sx={{ px: '4px', py: '1px', borderRadius: '4px', background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.06)', fontSize: 9, fontFamily: 'monospace', color: 'var(--text-disabled)' }}>{item.width}×{item.height}</Box>}
                      {item.videoCodec && <Box sx={{ px: '4px', py: '1px', borderRadius: '4px', background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.06)', fontSize: 9, fontFamily: 'monospace', color: 'var(--text-disabled)', textTransform: 'uppercase' }}>{item.videoCodec}</Box>}
                      {item.sizeBytes && item.sizeBytes > 0 && <Box sx={{ px: '4px', py: '1px', borderRadius: '4px', background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.06)', fontSize: 9, fontFamily: 'monospace', color: 'var(--text-disabled)' }}>{fmtSizeMB(item.sizeBytes)}</Box>}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Box sx={{ display: 'flex', gap: '2px' }}>
                        {(['fill','fit','stretch'] as const).map(mode => (
                          <Box key={mode} component="button" onClick={e => { e.stopPropagation(); setScale(idx, mode); }} onMouseDown={e => e.stopPropagation()}
                            sx={{ px: '5px', py: '2px', borderRadius: '4px', fontSize: 10, fontWeight: 600, fontFamily: 'inherit', border: `1px solid ${item.scaleMode===mode?'rgba(99,102,241,.35)':'rgba(255,255,255,.07)'}`, background: item.scaleMode===mode?'rgba(99,102,241,.18)':'transparent', color: item.scaleMode===mode?'var(--accent-light)':'var(--text-disabled)', cursor: 'pointer', transition: 'all .1s', '&:hover': item.scaleMode===mode?{}:{ color: 'var(--text-secondary)', borderColor: 'rgba(255,255,255,.15)' } }}>{mode}</Box>
                        ))}
                      </Box>
                      {!vid && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }} onMouseDown={e => e.stopPropagation()}>
                          <Box sx={{ width: 1, height: 14, background: 'var(--divider)', mx: '2px' }} />
                          <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'var(--text-disabled)' }}>DUR</Typography>
                          {[-10000,-1000].map(d => (
                            <Box key={d} component="button" onClick={e => { e.stopPropagation(); adjDur(idx, d); }}
                              sx={{ width: 18, height: 18, border: '1px solid rgba(255,255,255,.07)', background: 'transparent', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 12, fontFamily: 'monospace', color: 'var(--text-disabled)', '&:hover': { background: 'rgba(99,102,241,.15)', color: 'var(--accent-light)' } }}>−</Box>
                          ))}
                          <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'monospace', minWidth: 36, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>{fmtMs(dur)}</Typography>
                          {[1000,10000].map(d => (
                            <Box key={d} component="button" onClick={e => { e.stopPropagation(); adjDur(idx, d); }}
                              sx={{ width: 18, height: 18, border: '1px solid rgba(255,255,255,.07)', background: 'transparent', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 12, fontFamily: 'monospace', color: 'var(--text-disabled)', '&:hover': { background: 'rgba(99,102,241,.15)', color: 'var(--accent-light)' } }}>+</Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {inGroup && (
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: rot === 0 ? '#34d399' : 'var(--text-disabled)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {t('playlists2.pass', { n: rot + 1 })}
                    </Typography>
                  )}

                  {/* Duration badge (video) */}
                  {vid && (
                    <Box sx={{ px: '7px', py: '3px', borderRadius: '6px', background: 'rgba(99,102,241,.08)', border: '1px solid rgba(99,102,241,.15)', flexShrink: 0 }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--accent-light)', fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}>{fmtMs(dur)}</Typography>
                    </Box>
                  )}

                  {/* Calendar + remove — hover on parent to keep popup reachable */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', flexShrink: 0, position: 'relative' }}
                    onMouseDown={e => e.stopPropagation()}
                    onMouseEnter={() => { setCalHover(idx); fetchCalInfo(item.mediaFileId); }}
                    onMouseLeave={() => setCalHover(null)}>
                    <ActionBtn onClick={() => removeItem(idx)} title={t('common.delete')} danger><IcoX /></ActionBtn>
                    <Box sx={{ width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '5px', border: `1px solid ${calCache[item.mediaFileId] ? 'rgba(217,119,6,.45)' : 'var(--glass-border)'}`, background: calCache[item.mediaFileId] ? 'rgba(217,119,6,.1)' : 'var(--glass-bg)', color: calCache[item.mediaFileId] ? '#d97706' : 'var(--text-disabled)', cursor: 'default', flexShrink: 0, transition: 'all .15s', '&:hover': { background: 'rgba(99,102,241,.12)', color: 'var(--accent-light)', borderColor: 'rgba(99,102,241,.3)' } }}>
                      <IcoCal />
                    </Box>
                    {calHover === idx && <AvailabilityPopup fileId={item.mediaFileId} />}
                  </Box>
                </Box>}

                {/* Gap spacer between groups: expanded → after last item; collapsed → after group header */}
                {inGroup && isGroupEnd && !collapsedGroups.has(gk!) && (
                  <Box
                    onDragOver={e => { e.preventDefault(); e.stopPropagation(); setGapGk(gk!); setDropTarget(null); }}
                    onDragLeave={e => { if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) setGapGk(null); }}
                    onDrop={e => handleGapDrop(e, idx)}
                    sx={{ height: 4, minHeight: 4, mt: '-3px', flexShrink: 0, mx: '4px', borderRadius: '4px', position: 'relative',
                      ...(gapGk === gk ? { background: 'rgba(99,102,241,.06)' } : {}) }}>
                    {gapGk === gk && (
                      <Box sx={{ position: 'absolute', top: '50%', left: 8, right: 8, height: 2, borderRadius: 1,
                        background: 'var(--accent)', boxShadow: '0 0 8px var(--accent)', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                    )}
                  </Box>
                )}
                </React.Fragment>
              );
            })}
          </Box>

          {/* Footer */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', px: '12px', py: '8px', borderTop: `1px solid ${selectedItems.size > 0 ? 'rgba(99,102,241,.18)' : 'var(--divider)'}`, background: selectedItems.size > 0 ? 'rgba(99,102,241,.06)' : 'var(--glass-hover)', flexShrink: 0, flexWrap: 'wrap', transition: 'background .2s' }}>
            {selectedItems.size > 0 ? (
              <>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--accent-light)', flexShrink: 0 }}>{t('playlists2.selected', { count: selectedItems.size })}</Typography>
                <Box sx={{ width: 1, height: 14, background: 'rgba(99,102,241,.25)', flexShrink: 0 }} />
                {selectedItems.size > 1 && (
                  <Box component="button" onClick={groupSelected} title={t('playlists2.groupTooltip')}
                    sx={{ display: 'flex', alignItems: 'center', gap: '4px', px: '8px', py: '3px', borderRadius: '6px', flexShrink: 0,
                      border: '1px solid rgba(217,119,6,.35)', background: 'rgba(217,119,6,.12)', color: '#d97706',
                      fontSize: 10, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
                      '&:hover': { background: 'rgba(217,119,6,.2)' } }}>
                    🗂 {t('playlists2.group')}
                  </Box>
                )}
                {/* Action selector */}
                <Box component="select" value={bulkAction} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setBulkAction(e.target.value as 'scale' | 'duration' | '')}
                  sx={{ px: '6px', py: '2px', borderRadius: '5px', fontSize: 10, fontWeight: 600, fontFamily: 'inherit', background: 'rgba(99,102,241,.1)', border: '1px solid rgba(99,102,241,.25)', color: 'var(--accent-light)', cursor: 'pointer', outline: 'none', flexShrink: 0 }}>
                  <option value="">{t('playlists2.change')}</option>
                  <option value="scale">{t('playlists2.scaleName')}</option>
                  <option value="duration">{t('playlists2.durationName')}</option>
                </Box>
                {/* Scale controls */}
                {bulkAction === 'scale' && (['fill','fit','stretch'] as const).map(mode => (
                  <Box key={mode} component="button" onClick={() => { bulkSetScale(mode); setBulkAction(''); }}
                    sx={{ px: '7px', py: '2px', borderRadius: '5px', fontSize: 10, fontWeight: 600, fontFamily: 'inherit', border: '1px solid rgba(99,102,241,.3)', background: 'rgba(99,102,241,.1)', color: 'var(--accent-light)', cursor: 'pointer', flexShrink: 0, transition: 'all .1s', '&:hover': { background: 'rgba(99,102,241,.25)' } }}>
                    {mode}
                  </Box>
                ))}
                {/* Duration controls */}
                {bulkAction === 'duration' && (
                  <>
                    <Box component="input" type="number" min={1} step={1} placeholder={t('playlists2.secPlaceholder')} value={bulkDurSec}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBulkDurSec(e.target.value)}
                      onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') { bulkSetDuration(); setBulkAction(''); } }}
                      sx={{ width: 52, px: '5px', py: '2px', borderRadius: '5px', fontSize: 10, fontWeight: 700, fontFamily: 'monospace', background: 'rgba(99,102,241,.08)', border: '1px solid rgba(99,102,241,.2)', color: 'var(--accent-light)', textAlign: 'center', outline: 'none', flexShrink: 0, '&:focus': { borderColor: 'var(--accent)' }, '&::placeholder': { color: 'var(--text-disabled)', fontWeight: 400 } }} />
                    <Box component="button" onClick={() => { bulkSetDuration(); setBulkAction(''); }}
                      sx={{ px: '7px', py: '2px', borderRadius: '5px', fontSize: 10, fontWeight: 600, fontFamily: 'inherit', border: '1px solid rgba(99,102,241,.3)', background: 'rgba(99,102,241,.1)', color: 'var(--accent-light)', cursor: 'pointer', flexShrink: 0, '&:hover': { background: 'rgba(99,102,241,.25)' } }}>
                      {t('playlists2.apply')}
                    </Box>
                  </>
                )}
                <Box sx={{ flex: 1 }} />
                <Box component="button" onClick={bulkRemove}
                  sx={{ display: 'flex', alignItems: 'center', gap: '3px', px: '8px', py: '3px', borderRadius: '5px', border: '1px solid rgba(248,113,113,.25)', background: 'rgba(248,113,113,.06)', color: '#f87171', fontSize: 10, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', flexShrink: 0, '&:hover': { background: 'rgba(248,113,113,.15)' } }}>
                  <IcoX /> {t('common.delete')}
                </Box>
                <Box component="button" onClick={() => { clearSelection(); setBulkAction(''); }}
                  sx={{ px: '7px', py: '3px', borderRadius: '5px', border: '1px solid rgba(99,102,241,.2)', background: 'transparent', color: 'var(--text-secondary)', fontSize: 10, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', flexShrink: 0, '&:hover': { background: 'rgba(99,102,241,.08)' } }}>
                  ✕ {t('playlists2.deselect')}
                </Box>
              </>
            ) : (
              <>
                <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>{items.length} items</Typography>
                <Box sx={{ flex: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', px: '8px', py: '2px', borderRadius: '6px', background: 'rgba(255,255,255,.04)', border: '1px solid var(--glass-border)', flexShrink: 0 }}>
                  <IcoClock />
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}>{fmtMs(totalDurMs)}</Typography>
                </Box>
                <Box component="button" onClick={() => setItems([])}
                  sx={{ px: '9px', py: '4px', borderRadius: '6px', border: '1px solid rgba(248,113,113,.2)', background: 'rgba(248,113,113,.04)', color: '#f87171', fontSize: 11, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', '&:hover': { background: 'rgba(248,113,113,.12)' } }}>
                  Clear All
                </Box>
              </>
            )}
            <Box component="button" onClick={() => setMobileBrowserOpen(true)}
              sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: '4px', px: '9px', py: '4px', borderRadius: '7px', background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', flexShrink: 0, '&:hover': { color: 'var(--text-primary)', borderColor: 'var(--glass-border-strong)' } }}>
              <IcoPlus /> Add from Library
            </Box>
          </Box>
        </Box>

        {/* RIGHT: media browser (desktop only) */}
        <Box sx={{ flexShrink: 0, width: '40%', overflow: 'hidden', display: { xs: 'none', md: 'flex' }, flexDirection: 'column' }}>
          <MediaBrowserPanel onFileDoubleClick={addToPlaylist} onFilesAdd={files => files.forEach(addToPlaylist)} onFileDragStart={onMediaDragStart} onDragEnd={onMediaDragEnd} showDragHint />
        </Box>
      </Box>

      {/* Mobile library overlay */}
      {mobileBrowserOpen && (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 1400, background: 'var(--glass-bg)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', px: '14px', py: '10px', borderBottom: '1px solid var(--divider)', flexShrink: 0, gap: '10px' }}>
            <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Add from Library</Typography>
            <Box component="button" onClick={() => setMobileBrowserOpen(false)}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: '7px', background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', color: 'var(--text-disabled)', cursor: 'pointer', fontSize: 16, '&:hover': { color: 'var(--text-primary)' } }}>
              ✕
            </Box>
          </Box>
          <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <MediaBrowserPanel
              onFileDoubleClick={(file) => { addToPlaylist(file); setMobileBrowserOpen(false); }}
              onFilesAdd={files => { files.forEach(addToPlaylist); setMobileBrowserOpen(false); }}
              onFileDragStart={onMediaDragStart}
              onDragEnd={onMediaDragEnd}
            />
          </Box>
        </Box>
      )}
    </Box>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // VIEW: SMART GENERATOR
  // ═══════════════════════════════════════════════════════════════════════════

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ flex: 1, display: 'flex', gap: '10px', overflow: 'hidden', p: { xs: 0, md: '8px 14px 14px' } }}>

        {/* LEFT: smart generator */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--glass-bg)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: { xs: 'none', md: '1px solid rgba(168,85,247,.2)' }, borderRadius: { xs: 0, md: '14px' }, outline: dropZoneActive ? '2px solid #a855f7' : '2px solid transparent', transition: 'outline-color .15s' }}
          onDragOver={onSmartZoneDragOver} onDrop={onSmartZoneDrop} onDragLeave={() => setDropZoneActive(false)}>

          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', px: { xs: '10px', md: '14px' }, py: '8px', borderBottom: '1px solid rgba(168,85,247,.12)', background: 'rgba(168,85,247,.04)', flexShrink: 0 }}>
            {/* Back + title already sit in the topbar on mobile — desktop only */}
            <Box component="button" onClick={() => setView('editor')}
              sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: '5px', px: '8px', py: '4px', borderRadius: '7px', background: 'rgba(168,85,247,.06)', border: '1px solid rgba(168,85,247,.2)', color: '#c084fc', fontSize: 11, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', flexShrink: 0, '&:hover': { background: 'rgba(168,85,247,.14)', color: 'var(--text-primary)' } }}>
              <IcoBack /> Editor
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'block' }, width: '1px', height: 14, background: 'rgba(168,85,247,.25)', flexShrink: 0 }} />
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', flexShrink: 0 }}><IcoStar /></Box>
            <Typography sx={{ display: { xs: 'none', md: 'block' }, fontSize: 12, fontWeight: 700, color: '#c084fc', textTransform: 'uppercase', letterSpacing: '.07em', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Smart Generator</Typography>
            {smartTotalFiles > 0 && (
              <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <Box sx={{ px: '8px', py: '2px', borderRadius: '8px', background: 'rgba(168,85,247,.1)', border: '1px solid rgba(168,85,247,.2)' }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#c084fc', fontVariantNumeric: 'tabular-nums' }}>{smartTotalFiles} items</Typography>
                </Box>
                <Box sx={{ px: '8px', py: '2px', borderRadius: '8px', background: 'rgba(168,85,247,.07)', border: '1px solid rgba(168,85,247,.15)' }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#a78bfa', fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}>{fmtMs(smartActualTotalMs)}</Typography>
                </Box>
                {smartActualTotalMs < smartTotalMs && (
                  <Box sx={{ px: '7px', py: '2px', borderRadius: '8px', background: 'rgba(168,85,247,.05)', border: '1px solid rgba(168,85,247,.12)' }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, fontFamily: 'monospace', color: 'rgba(168,85,247,.6)', fontVariantNumeric: 'tabular-nums' }}>
                      {t('playlists2.occupied', { pct: Math.round(smartActualTotalMs / smartTotalMs * 100) })}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/* Settings panel */}
          <Box sx={{ px: '14px', py: '10px', borderBottom: '1px solid rgba(168,85,247,.1)', background: 'rgba(168,85,247,.025)', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '20px' }}>
            {/* Duration */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
              <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--text-disabled)', flexShrink: 0 }}>Duration</Typography>
              <Box component="input" type="range" min={1} max={30} value={smartMinutes} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSmartMinutes(+e.target.value)} sx={{ flex: 1, accentColor: '#a855f7', cursor: 'pointer', height: 4 }} />
              <Typography sx={{ fontSize: 12, fontWeight: 700, fontFamily: 'monospace', color: '#c084fc', minWidth: 44, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{smartMinutes} min</Typography>
            </Box>
          </Box>

          {/* File list header */}
          <Box sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: '1fr 180px 52px 80px 20px', px: '12px', py: '5px', borderBottom: '1px solid var(--divider)', background: 'rgba(0,0,0,.07)', flexShrink: 0, gap: '8px' }}>
            {['File', t('playlists2.countCol'), 'Plays', 'Duration'].map((h, i) => (
              <Typography key={i} sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-disabled)', textAlign: i >= 2 ? 'right' : 'left' }}>{h}</Typography>
            ))}
            <Typography sx={{ fontSize: 10, color: 'transparent' }}>×</Typography>
          </Box>

          {/* File rows */}
          <Box sx={{ flex: 1, overflowY: 'auto', '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,.08)', borderRadius: 4 }, ...scrollAreaSx }}>
            {smartFiles.map((sf, i) => {
              const plays = smartCalcN(sf);
              const maxP = smartMaxPlays(i);
              const isManual = sf.manualPlays > 0;
              return (
                <Box key={i} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr auto 20px', md: '1fr 180px 52px 80px 20px' }, gridTemplateAreas: { xs: '"name plays remove" "bar bar dur"', md: 'none' }, px: { xs: '10px', md: '12px' }, py: '6px', alignItems: 'center', borderBottom: '1px solid var(--divider)', gap: { xs: '4px 8px', md: '8px' }, transition: 'background .1s', '&:hover': { background: 'rgba(168,85,247,.04)' } }}>
                  {/* File name + icon */}
                  <Box sx={{ gridArea: { xs: 'name', md: 'auto' }, display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                    <Box sx={{ width: 28, height: 18, borderRadius: '3px', background: sf.video ? 'linear-gradient(135deg,#071a30,#0f3d6e)' : 'linear-gradient(135deg,#064e3b,#0e7c5a)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {sf.video ? <IcoVideo /> : <IcoImage />}
                    </Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 500, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sf.name}</Typography>
                  </Box>
                  {/* Switch + bar */}
                  <Box sx={{ gridArea: { xs: 'bar', md: 'auto' }, display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                    {/* Toggle switch: ON = auto, OFF = manual */}
                    <Box component="button" title={t('playlists2.autoMode')}
                      onClick={() => sToggleAuto(i)}
                      sx={{ width: 28, height: 16, borderRadius: '8px', flexShrink: 0, cursor: 'pointer', border: 'none', position: 'relative', background: !isManual ? '#a855f7' : 'rgba(255,255,255,.12)', transition: 'background .18s', padding: 0, outline: 'none', '&:hover': { outline: '1px solid rgba(168,85,247,.4)' } }}>
                      <Box sx={{ position: 'absolute', top: 2, width: 12, height: 12, borderRadius: '50%', background: 'white', transition: 'left .18s', left: !isManual ? '14px' : '2px' }} />
                    </Box>
                    {/* Bar: disabled in auto mode */}
                    <Box component="input" type="range" min={0} max={maxP} step={1}
                      value={isManual ? sf.manualPlays : plays}
                      disabled={!isManual}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => sSetManual(i, +e.target.value)}
                      sx={{ flex: 1, accentColor: '#a855f7', cursor: isManual ? 'pointer' : 'default', height: 4, opacity: isManual ? 1 : 0.25, pointerEvents: isManual ? 'auto' : 'none' }} />
                    <Box sx={{ minWidth: 28, textAlign: 'right' }}>
                      {isManual && <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#c084fc', fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}>{sf.manualPlays}</Typography>}
                    </Box>
                  </Box>
                  {/* Plays count */}
                  <Typography sx={{ gridArea: { xs: 'plays', md: 'auto' }, fontSize: 13, fontWeight: 700, color: isManual ? '#c084fc' : 'var(--text-primary)', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>×{plays}</Typography>
                  {/* Duration — editable only for images, at end */}
                  {sf.video
                    ? <Typography sx={{ gridArea: { xs: 'dur', md: 'auto' }, fontSize: 10, fontFamily: 'monospace', color: 'var(--text-disabled)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{Math.round(sf.durationMs / 1000)}s</Typography>
                    : <Box sx={{ gridArea: { xs: 'dur', md: 'auto' }, display: 'flex', alignItems: 'center', gap: '2px', justifyContent: 'flex-end' }}>
                        <Box component="button" onClick={() => setSmartFiles(prev => prev.map((f,j) => j!==i?f:{...f,durationMs:Math.max(1000,f.durationMs-1000)}))}
                          sx={{ width: 14, height: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '3px', border: '1px solid rgba(168,85,247,.2)', background: 'rgba(168,85,247,.06)', color: '#c084fc', cursor: 'pointer', fontSize: 9, '&:hover': { background: '#a855f7', color: '#fff' } }}>−</Box>
                        <Box component="input" type="number" min={1} value={Math.round(sf.durationMs / 1000)}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const sec = Math.max(1, Math.round(+e.target.value));
                            if (!isNaN(sec)) setSmartFiles(prev => prev.map((f,j) => j!==i ? f : {...f, durationMs: sec * 1000}));
                          }}
                          onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                            const sec = Math.max(1, Math.round(+e.target.value || 1));
                            setSmartFiles(prev => prev.map((f,j) => j!==i ? f : {...f, durationMs: sec * 1000}));
                          }}
                          sx={{ width: 32, px: '2px', py: '1px', borderRadius: '4px', fontSize: 10, fontWeight: 700, fontFamily: 'monospace', background: 'rgba(168,85,247,.06)', border: '1px solid rgba(168,85,247,.2)', color: '#c084fc', textAlign: 'center', outline: 'none', fontVariantNumeric: 'tabular-nums', '&:focus': { borderColor: '#a855f7' }, MozAppearance: 'textfield', '&::-webkit-outer-spin-button': { display: 'none' }, '&::-webkit-inner-spin-button': { display: 'none' } }} />
                        <Typography sx={{ fontSize: 9, color: 'rgba(168,85,247,.5)' }}>s</Typography>
                        <Box component="button" onClick={() => setSmartFiles(prev => prev.map((f,j) => j!==i?f:{...f,durationMs:f.durationMs+1000}))}
                          sx={{ width: 14, height: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '3px', border: '1px solid rgba(168,85,247,.2)', background: 'rgba(168,85,247,.06)', color: '#c084fc', cursor: 'pointer', fontSize: 9, '&:hover': { background: '#a855f7', color: '#fff' } }}>+</Box>
                      </Box>
                  }
                  {/* Remove */}
                  <Box component="button" onClick={() => sRemove(i)}
                    sx={{ gridArea: { xs: 'remove', md: 'auto' }, width: 20, height: 20, borderRadius: '4px', border: 'none', background: 'transparent', color: 'var(--text-disabled)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', opacity: .35, transition: 'all .12s', '&:hover': { opacity: 1, color: '#f87171', background: 'rgba(248,113,113,.1)' } }}>
                    <IcoX />
                  </Box>
                </Box>
              );
            })}
            {smartFiles.length === 0 && (
              <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)', py: '30px', textAlign: 'center' }}>Drag a file from the library or double-click to add</Typography>
            )}
          </Box>

          {/* Footer */}
          <Box sx={{ flexShrink: 0, position: 'relative' }}>
            {smartDupWarn && (
              <Box sx={{ position: 'absolute', bottom: '100%', left: 0, right: 0, px: '12px', py: '6px', background: 'rgba(234,179,8,.12)', borderTop: '1px solid rgba(234,179,8,.25)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="#eab308"><path d="M12 2L1 21h22L12 2zm0 3.5L20.5 19h-17L12 5.5zM11 10v4h2v-4h-2zm0 6v2h2v-2h-2z"/></svg>
                <Typography sx={{ fontSize: 11, color: '#eab308' }}>Already in playlist: {smartDupWarn}</Typography>
              </Box>
            )}
          </Box>
          {/* Remaining time bar — shown while there is unused time */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', px: '12px', py: '8px', borderTop: '1px solid rgba(168,85,247,.12)', background: 'rgba(168,85,247,.03)', flexShrink: 0 }}>
            <Box component="button" onClick={() => setMobileBrowserOpen(true)}
              sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: '4px', px: '9px', py: '5px', borderRadius: '7px', background: 'rgba(168,85,247,.06)', border: '1px solid rgba(168,85,247,.2)', color: '#c084fc', fontSize: 11, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', flexShrink: 0, '&:hover': { background: 'rgba(168,85,247,.14)', color: 'var(--text-primary)' } }}>
              <IcoPlus /> Add from Library
            </Box>
            <Box sx={{ flex: 1 }} />
            <Box component="button" onClick={() => setView('editor')}
              sx={{ px: '10px', py: '4px', borderRadius: '6px', background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', '&:hover': { background: 'var(--glass-hover)', color: 'var(--text-primary)' } }}>
              Cancel
            </Box>
            <Box component="button" onClick={doGenerate}
              sx={{ display: 'flex', alignItems: 'center', gap: '5px', px: '14px', py: '6px', borderRadius: '7px', background: 'linear-gradient(135deg,rgba(168,85,247,.9),rgba(192,38,211,.8))', border: 'none', color: '#fff', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', boxShadow: '0 2px 8px rgba(168,85,247,.35)', '&:hover': { opacity: .88 } }}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
              Generate {smartTotalFiles > 0 ? `(${smartTotalFiles} files)` : 'Playlist'}
            </Box>
          </Box>
        </Box>

        {/* RIGHT: media browser (desktop only) */}
        <Box sx={{ flexShrink: 0, width: '40%', overflow: 'hidden', display: { xs: 'none', md: 'flex' }, flexDirection: 'column' }}>
          <MediaBrowserPanel onFileDoubleClick={addSmartFromMedia} onFilesAdd={files => files.forEach(addSmartFromMedia)} onFileDragStart={onMediaDragStart} onDragEnd={onMediaDragEnd} showDragHint />
        </Box>
      </Box>

      {/* Mobile library overlay */}
      {mobileBrowserOpen && (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 1400, background: 'var(--glass-bg)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', px: '14px', py: '10px', borderBottom: '1px solid var(--divider)', flexShrink: 0, gap: '10px' }}>
            <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Add from Library</Typography>
            <Box component="button" onClick={() => setMobileBrowserOpen(false)}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: '7px', background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', color: 'var(--text-disabled)', cursor: 'pointer', fontSize: 16, '&:hover': { color: 'var(--text-primary)' } }}>
              ✕
            </Box>
          </Box>
          <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <MediaBrowserPanel
              onFileDoubleClick={(file) => { addSmartFromMedia(file); setMobileBrowserOpen(false); }}
              onFilesAdd={files => { files.forEach(addSmartFromMedia); setMobileBrowserOpen(false); }}
              onFileDragStart={onMediaDragStart}
              onDragEnd={onMediaDragEnd}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PlaylistsPage2;
